import React, { useEffect, useState, useRef } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionPad, Badge } from "../../components/ui-extras";
import BulkUploader from "../../components/BulkUploader";
import { UploadSimple, Funnel, DownloadSimple, Trash, Info, CheckCircle, PencilSimple, Warning, Clock, FileText } from "@phosphor-icons/react";
import { startDownload, backendDownloadUrl, downloadFromBackend } from "../../lib/download";
import { displayProduct } from "../../lib/products";
import { useJobPolling } from "../../lib/useJobPolling";
import { JobProgress } from "../../components/JobProgress";
import UndoBanner from "../../components/UndoBanner";
import { PreflightPanel, reconcileImport } from "../../components/PreflightPanel";
import { useWorkerHealth, WorkerOfflineNotice } from "../../components/WorkerHealth";

import { formatCard } from "../../lib/cardDisplay";
import { Can } from "../../lib/rbac";
import { StickyHScroll } from "../../components/StickyHScroll";

// P4-N5B Slice C2 — canonical Network/Country adapter (C1 foundation).
// Consumed ONLY by the Download Customer Report modal in this slice;
// unrelated Transactions.jsx surfaces (import lane picker, ledger filter,
// small `Download transaction report` modal) remain unchanged.
import {
  COUNTRY_LABELS,
  TRANSACTION_CAPABILITY,
  getVisibleCountries,
  toWireParams,
} from "../../lib/networkCountryCatalog";

// R2 — display-only currency/unit labels (values untouched).
const LANE_UNITS = {
  CA: { qty: "L", rate: "CAD/L", mrg: "¢/L", amt: "CAD" },
  US: { qty: "GAL", rate: "USD/GAL", mrg: "¢/GAL", amt: "USD" },
};
const rowLane = (t) => (String(t.currency || "").toUpperCase() === "USD" || (!t.currency && t.country === "US") ? "US" : "CA");
const tableLane = (rows, filters) => {
  const lanes = new Set(rows.map(rowLane));
  if (lanes.size === 1) return [...lanes][0];
  if (lanes.size > 1) return "MIXED";
  if (filters.network === "petro_pass_us" || filters.country === "US") return "US";
  if (filters.network === "petro_pass_ca" || filters.network === "pipeline" || filters.country === "CA") return "CA";
  return "MIXED";
};
const LANE_NAME = { CA: "Canada", US: "USA" };
const laneTotals = (rows) => rows.reduce((a, r) => {
  const l = rowLane(r);
  a[l] = a[l] || { qty: 0, amt: 0, gp: 0 };
  a[l].qty += r.qty || 0; a[l].amt += r.customer_amount || 0; a[l].gp += r.gross_profit || 0;
  return a;
}, {});
const LaneSummary = ({ lane, t, prefix }) => (
  <div className="grid grid-cols-3 gap-2 text-center">
    {[["qty", "QTY", `${t.qty.toFixed(0)} ${lane === "US" ? "gal" : "L"}`],
      ["rev", "REV", `$${t.amt.toFixed(0)}`],
      ["gp", "GP", `$${t.gp.toFixed(0)}`]].map(([k, lbl, val]) => (
      <div key={k} className="gf-stat" style={{ padding: 10 }} data-testid={`${prefix}-${k}`}>
        <div className="overline">{lbl} ({k === "qty" ? LANE_UNITS[lane].qty : LANE_UNITS[lane].amt})</div>
        <div className="font-mono font-semibold">{val}</div>
      </div>
    ))}
  </div>
);
const colLabel = (lane, key) => (lane === "MIXED" ? "" : ` (${LANE_UNITS[lane][key]})`);
const RowUnit = ({ lane, t, k }) => (lane === "MIXED" ? <div className="text-[9px] text-zinc-400 font-mono" data-testid={`tx-unit-${k}-${t.transaction_id}`}>{LANE_UNITS[rowLane(t)][k]}</div> : null);

// "All networks" branch — union of every provider's transaction-country
// capability. Consumes C1's TRANSACTION_CAPABILITY matrix directly; does
// NOT duplicate the per-network capability rules inside this file.
const CUSTOMER_REPORT_ALL_NETWORKS_COUNTRIES = (() => {
  const union = Array.from(
    new Set(Object.values(TRANSACTION_CAPABILITY).flat())
  );
  return union.length > 1 ? ["", ...union] : union;
})();

// Country options visible in the Download Customer Report modal for a
// given selected Network. Delegates to C1's `getVisibleCountries` for
// specific networks; falls back to the "All networks" union above when
// the operator has NOT selected a network.
function _customerReportCountriesFor(network) {
  if (!network) return CUSTOMER_REPORT_ALL_NETWORKS_COUNTRIES;
  return getVisibleCountries("transactions", network);
}

// P4-N5B Slice C5 R1 — Ledger main Filters row Network/Country capability
// helper. Uses the SAME C1 primitives as the C2 helper above but is
// namespaced separately for the Ledger surface (accepted C2 modal region
// is untouched by C5; the two helpers happen to compute the same union
// today but stay independent so future capability drift on either
// surface can proceed without cross-coupling).
function _ledgerCountriesFor(network) {
  if (!network) return CUSTOMER_REPORT_ALL_NETWORKS_COUNTRIES;
  return getVisibleCountries("transactions", network);
}

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

export default function Transactions() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [reportPickerCustomers, setReportPickerCustomers] = useState([]);
  const [uploads, setUploads] = useState([]);
  // Guardrail (May 2026): /uploads/integrity-report surfaces uploads
  // whose manifest of imported transaction_ids has drifted from the
  // live transactions collection. Loaded once on mount + after every
  // import/delete so admins see silent disappearance immediately.
  const [integrityProblems, setIntegrityProblems] = useState([]);
  // 2026-05-22 operator spec: Province + Product filter dropdowns are
  // scoped to a fixed per-network whitelist (operator's published spec)
  // — NOT a distinct query of what's currently in the DB. That keeps
  // empty months and one-off imports out of the dropdown noise.
  const PROVINCES_BY_NETWORK = {
    esso: ["AB", "BC", "MB", "NS", "ON", "QC", "SK"],
    pipeline: ["AB", "BC", "NB", "NL", "NS", "ON", "QC"],
  };
  // Product is shown with its canonical label. Each label translates
  // to a backend query that handles every raw spelling seen in the
  // source files (Pipeline says "Diesel", ESSO says "ULSD" or "DIESEL"
  // — all roll up to canonical DIESEL_LS or DSL_EFF_LS).
  // For ESSO, RFR is its own raw label (canonical=DSL_EFF_LS) so we
  // filter on the literal product to keep it distinct.
  const PRODUCTS_BY_NETWORK = {
    esso: ["Diesel LS", "DSL EFF LS", "DEFD", "RFR"],
    pipeline: ["Diesel LS", "DEFD"],
  };
  // When network is empty we offer the UNION so the operator can still
  // narrow down before picking a network.
  const allProvinces = Array.from(new Set([...PROVINCES_BY_NETWORK.esso, ...PROVINCES_BY_NETWORK.pipeline])).sort();
  const allProducts = Array.from(new Set([...PRODUCTS_BY_NETWORK.esso, ...PRODUCTS_BY_NETWORK.pipeline]));
  const [filters, setFilters] = useState({ customer_id: "", network: "", country: "", province: "", product: "", status: "", unit: "", start: "", end: "", upload_id: "" });
  const txLane = tableLane(rows, filters);
  const [showImport, setShowImport] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showCustomerReport, setShowCustomerReport] = useState(false);
  const [customerReportForm, setCustomerReportForm] = useState({
    date_from: "", date_to: "",
    customer_ids: [],
    network: "",
    country: "",
    file_type: "csv",
    search: "",
  });
  // Seed the customer-safe report modal dates (last 30 days) after the
  // date helpers are in scope. Placed here to avoid `daysAgo` hoisting
  // issues in state initialisers.
  useEffect(() => {
    setCustomerReportForm((f) => ({
      ...f,
      date_from: f.date_from || daysAgo(30),
      date_to:   f.date_to   || today(),
    }));
  }, []);
  const [showUploads, setShowUploads] = useState(false);
  const [showFlagHelp, setShowFlagHelp] = useState(false);
  const [reportForm, setReportForm] = useState({ date_from: daysAgo(30), date_to: today(), customer_id: "", network: "esso", country: "" });
  const [network, setNetwork] = useState("esso");
  // Country selector — visible only for ESSO (USA vs Canada file layouts
  // are completely different due to taxation). Pipeline is Canada-only.
  const [country, setCountry] = useState("CA");
  const [customer_id, setCustomerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // ─ Free-form text the operator must type into the upload-delete modal
  //   to arm the destructive request. The backend's `guard_destructive`
  //   accepts this exact string (see backend/safety.py).
  const REQUIRED_UPLOAD_DELETE_PHRASE = "I UNDERSTAND THIS IS IRREVERSIBLE";
  const [confirmDeleteText, setConfirmDeleteText] = useState("");
  const [repricing, setRepricing] = useState(false);

  // Bulk-delete state. `mode` is "filtered" (uses current sidebar filters) or
  // "all" (full reset). `preview` carries the dry-run counts so the user can
  // see exactly what will go before they type DELETE.
  const [bulkDelete, setBulkDelete] = useState(null); // { mode, preview, force, confirmText, busy }
  // Currency/UOM mismatch guardrail (operator RCA 2026-07-09).
  // When null, no warning. When populated with an object, the confirm
  // dialog renders and the operator must click "Upload anyway" or "Cancel".
  const [currencyMismatchWarning, setCurrencyMismatchWarning] = useState(null);

  // Sticky 30s undo banner — populated after any soft-delete on this page
  // so the operator can one-click restore without leaving the screen.
  const [undoState, setUndoState] = useState(null);

  // Cause #2 hardening (2026-05-29) — typed-confirmation modal for the
  // Delete Duplicates flow. Holds the live preview from the
  // /transactions/duplicates/preview endpoint and the operator's typed
  // confirmation string. `scope` is "customer" or "all".
  //   Shape: { open, scope, customer_id, preview, typed, busy }
  // where preview = { transaction_count, customer_count, customers: [...] }
  const [purgeModal, setPurgeModal] = useState({
    open: false, scope: null, customer_id: null,
    preview: null, typed: "", busy: false,
  });

  // Cause #2 hardening — "Undo last duplicate purge" tile in the page
  // header. Populated by loadLastPurgeTile() at mount + after every
  // purge. null = tile hidden (no restorable purge available).
  const [lastPurge, setLastPurge] = useState(null);

  // Suspected-duplicate scan modal. `groups` is the server's grouped result;
  // `selected` is the set of transaction_ids the operator has ticked to mark
  // as duplicate. By default we pre-tick all-but-the-oldest row in each group
  // so the typical "keep the original, demote the rest" flow is one click.
  const [scanDupes, setScanDupes] = useState(null); // { groups, selected:Set, busy }

  // Manual status-change modal. Opens from the row "..." button and lets an
  // admin force a status transition (including assigning a customer to an
  // unmatched row, or voiding an invoiced row).
  const [statusEdit, setStatusEdit] = useState(null); // { tx, status, customer_id, busy }

  // Single-row delete confirm. `force` allows wiping an invoiced row + recomputing.
  const [delTx, setDelTx] = useState(null); // { tx, force, busy }
  // Phase 2.B — Failed-rows modal (one per upload). null = closed.
  // Shape: { upload, rows:[...], busy }
  const [failedRows, setFailedRows] = useState(null);
  // Phase 2.A — async-import job + polling.
  // Shape: { job_id }; the polling hook drives the rest of the UI.
  const [importJob, setImportJob] = useState(null);
  // PRE-IMPORT VALIDATION R1 — read-only server preflight gates Import.
  const [preflight, setPreflight] = useState(null);
  const [preflightAck, setPreflightAck] = useState(false);
  const [preflightBusy, setPreflightBusy] = useState(false);
  const expectedRef = useRef(null);
  const [importDone, setImportDone] = useState(null);
  const resetPreflight = () => { setPreflight(null); setPreflightAck(false); setImportDone(null); };
  useEffect(() => { resetPreflight(); }, [network, country, customer_id, showImport]); // eslint-disable-line
  const [useAsync, setUseAsync] = useState(true);
  const [workerOnline, setWorkerOnline] = useWorkerHealth(showImport);
  const fileRef = useRef();

  // 2026-05-22 — Re-import audit modal. After fixing a parser bug we
  // need to recover rows that earlier imports silently dropped. The
  // operator re-uploads the source CSV and the backend inserts ONLY
  // missing rows (dedup-safe).
  //   Shape: { open, file, network, country, dry_run, busy, result }
  const [auditModal, setAuditModal] = useState({
    open: false, file: null, network: "esso", country: "CA",
    dry_run: true, busy: false, result: null,
  });
  const auditFileRef = useRef();

  const openFailedRows = async (upload) => {
    setFailedRows({ upload, rows: [], busy: true });
    try {
      const { data } = await api.get(`/imports/${upload.upload_id}/failed-rows`);
      setFailedRows({ upload, rows: data.rows || [], busy: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load row details");
      setFailedRows(null);
    }
  };

  const load = async () => {
    const params = {};
    for (const k in filters) {
      if (!filters[k]) continue;
      // Country is kept for every lane including Network = All; dropped only
      // for pipeline (Canada-only by product definition).
      if (k === "country" && filters.network === "pipeline") continue;
      // Product dropdown carries the canonical label ("Diesel LS",
      // "DSL EFF LS", "DEFD", "RFR"). Translate it into the right
      // backend query param so 1 dropdown pick covers every raw
      // spelling that maps to the same fuel grade.
      if (k === "product") {
        const label = filters.product;
        if (label === "Diesel LS")        { params.product_canonical = "DIESEL_LS"; continue; }
        if (label === "DSL EFF LS")       { params.product_canonical = "DSL_EFF_LS"; continue; }
        if (label === "DEFD")             { params.product_canonical = "DEFD";       continue; }
        if (label === "RFR")              { params.product = "RFR";                  continue; }
        // Unknown label — pass through as-is.
        params.product = label;
        continue;
      }
      params[k] = filters[k];
    }
    const { data } = await api.get("/transactions", { params });
    setRows(data);
  };
  const loadUploads = async () => {
    try {
      const { data } = await api.get("/transactions/uploads");
      setUploads(data);
    } catch {
      // Users without transactions.upload get 403; upload list is not
      // useful to them anyway.
      setUploads([]);
    }
  };
  const loadIntegrity = async () => {
    try {
      const { data } = await api.get("/transactions/uploads/integrity-report");
      setIntegrityProblems(data.uploads || []);
    } catch {
      // Silent: integrity check is advisory, not blocking.
      setIntegrityProblems([]);
    }
  };
  useEffect(() => {
    // Full customer list (requires customers.view). Restricted users
    // get [] via the axios 403 interceptor.
    api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => setCustomers([]));
    // Slim id+name list for the customer-safe report modal. Any user
    // with transactions.download_customer_reports (or the full
    // customers.view perm) can call this — see /api/customers/for-report-picker.
    api.get("/customers/for-report-picker")
      .then(({ data }) => setReportPickerCustomers(Array.isArray(data) ? data : []))
      .catch(() => setReportPickerCustomers([]));
    load(); loadUploads(); loadIntegrity(); loadLastPurgeTile();
    /* eslint-disable-next-line */
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  // P4-N2D (2026-02) — Admin Unit filter dropdown wiring.
  // Historically-safe: reads from `/api/admin/units` (customer's
  // stored transaction.unit values, filtered by tran_date/network/
  // country), NOT from `/api/cards?customer_id` (current ownership
  // — would silently drop transferred cards' historical units, e.g.
  // JSONS Esso NR1001/NR1002 → Night Road). Pagination-safe: the
  // backend endpoint runs a Mongo distinct over the entire filtered
  // transaction set. Card-management surfaces (Cards tab) still
  // read current ownership via `/api/cards` — untouched.
  const [unitOptions, setUnitOptions] = useState([]);
  useEffect(() => {
    if (!filters.customer_id) { setUnitOptions([]); return; }
    const params = { customer_id: filters.customer_id };
    if (filters.start) params.date_from = filters.start;
    if (filters.end)   params.date_to   = filters.end;
    if (filters.network && filters.network !== "petro_pass_ca" && filters.network !== "petro_pass_us")
      params.network = filters.network;
    if (filters.network === "petro_pass_ca") { params.network = "petro_pass"; params.country = "CA"; }
    if (filters.network === "petro_pass_us") { params.network = "petro_pass"; params.country = "US"; }
    // P4-N5B Slice C5 R1 — `filters.network` now stores a canonical
    // provider ("esso" / "pipeline" / "petro_pass") — never `ta_petro`
    // and never a compound `petro_pass_ca` / `petro_pass_us`. Forward
    // the paired `country` for every network that has a country
    // capability (esso: CA+US, petro_pass: CA+US). Pipeline is CA-only
    // and Country is locked; `filters.country` for pipeline is either
    // empty or "CA" — either is acceptable.
    if ((filters.network === "esso" || filters.network === "petro_pass") && filters.country) params.country = filters.country;
    api.get("/admin/units", { params })
      .then((res) => setUnitOptions(Array.isArray(res?.data?.units) ? res.data.units : []))
      .catch(() => setUnitOptions([]));
  }, [filters.customer_id, filters.start, filters.end, filters.network, filters.country]);
  // If the current unit selection is no longer in the refreshed set,
  // clear it so we don't send a stale filter.
  useEffect(() => {
    if (filters.unit && unitOptions.length && !unitOptions.includes(filters.unit)) {
      setFilters((f) => ({ ...f, unit: "" }));
    }
  }, [unitOptions]); // eslint-disable-line

  const runPreflight = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose a file");
    const fd = new FormData();
    fd.append("network", network);
    fd.append("country", network === "pipeline" ? "CA" : country);
    if (customer_id) fd.append("customer_id", customer_id);
    fd.append("file", f);
    resetPreflight();
    setPreflightBusy(true);
    try {
      const { data } = await api.post("/transactions/preflight", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreflight(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || d || "Validation failed");
    } finally {
      setPreflightBusy(false);
    }
  };

  const upload = async (opts = {}) => {
    const forceCurrencyMismatch = !!opts.forceCurrencyMismatch;
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose a file");
    if (!preflight || !preflight.can_import) return toast.error("Validate the file before importing");
    if (preflight.requires_confirmation && !preflightAck) return toast.error("Confirm the review / failed rows first");
    const fd = new FormData();
    fd.append("network", network);
    // ESSO files come in two completely different layouts (USA vs Canada
    // due to taxation). Pipeline is Canada-only. Petro-Pass has separate
    // CA (Report31, CAD/L) and USA (Report25, USD/GAL) parsers as of
    // Phase PP-USA-REPORT25-PARSER-IMPL-R1 (Sep 2026) — operator's
    // country selection routes to the correct parser.
    const effectiveCountry = network === "pipeline" ? "CA" : country;
    fd.append("country", effectiveCountry);
    if (customer_id) fd.append("customer_id", customer_id);
    fd.append("file", f);
    if (forceCurrencyMismatch) fd.append("force_currency_mismatch", "true");
    fd.append("preflight_token", preflight.preflight_token);
    fd.append("preflight_id", preflight.preflight_id);
    if (preflightAck) fd.append("preflight_ack_partial", "true");
    expectedRef.current = preflight.expected;
    setBusy(true);
    try {
      if (useAsync && workerOnline) {
        // Phase 2.A — async path: enqueue, then poll.
        const { data } = await api.post("/transactions/import-async", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setImportJob({ job_id: data.job_id });
        setImportDone({ status: "queued" });
        toast.success(`Import queued · job ${data.job_id.slice(-6)}`);
      } else {
        // Legacy sync fallback (toggle in the import modal).
        const { data } = await api.post("/transactions/import", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const dupNote = data.duplicate_of ? " · note: same file uploaded before" : "";
        const dupTxNote = data.duplicates ? ` · duplicates ${data.duplicates}` : "";
        const syncDiffs = reconcileImport(expectedRef.current, {
          inserted: data.imported, duplicates: data.duplicates, failed: data.review_rows || 0,
        });
        expectedRef.current = null;
        const syncMsg = `Imported ${data.imported} · flagged ${data.flagged} · unmatched ${data.unmatched}${dupTxNote}${dupNote}`;
        if (syncDiffs.length) toast.error(`Import result differs from validation: ${syncDiffs.join("; ")}`, { duration: 20000 });
        else if ((data.review_rows || 0) > 0) toast.warning(`Completed with failures · ${syncMsg}`, { duration: 15000 });
        else toast.success(syncMsg);
        // P4-PETRO-PASS-UNKNOWN-PRODUCT-GUARD R1 (2026-09-16). Amber
        // warning surfacing: when the PP importer returns
        // `review_rows > 0` the import contained unsupported
        // Petro-Pass product(s). Recognised rows imported normally;
        // unsupported rows were retained in `failed_import_rows` and
        // never posted as guessed transactions. Operator can inspect
        // via the existing failed-rows viewer + reprocess through
        // `POST /api/admin/pp/reprocess_unmapped` once a mapping is
        // added.
        if (data.review_rows && data.review_rows > 0) {
          const rawProducts = Array.from(new Set(
            (data.review_samples || []).map((s) => s.product_raw).filter(Boolean),
          )).slice(0, 3).join(", ");
          const productBit = rawProducts ? ` — unsupported product(s): ${rawProducts}` : "";
          toast.warning(
            `${data.review_rows} row(s) retained for review${productBit}. ` +
            `Recognised rows imported normally; unsupported rows are NOT posted ` +
            `as transactions. Open the failed-rows viewer for details.`,
            { duration: 15000 }
          );
        }
        setImportDone({ status: "completed", inserted: data.imported, duplicates: data.duplicates || 0,
                        failed: data.review_rows || 0, diffs: syncDiffs });
        load(); loadUploads();
      }
    } catch (e) {
      // Currency/UOM mismatch guardrail — operator RCA 2026-07-09.
      // Backend returns 409 with detail.code === "CURRENCY_UOM_MISMATCH"
      // when an Esso USD/GAL upload has any rows tagged CAD/Liters instead
      // of USD/Gallons. We surface a confirm dialog: "VALUES ARE IN
      // CAD/LITRES — upload anyway?" — on OK, retry with the force flag.
      const detail = e.response?.data?.detail;
      if (e.response?.status === 503 && detail?.code === "WORKER_UNAVAILABLE") {
        setWorkerOnline(false);
        toast.error(detail.message, { duration: 12000 });
        return;
      }
      if (
        e.response?.status === 409
        && detail
        && typeof detail === "object"
        && detail.code === "CURRENCY_UOM_MISMATCH"
      ) {
        setCurrencyMismatchWarning({
          filename: f.name,
          rowsTotal: detail.rows_total,
          rowsMismatched: detail.rows_mismatched,
          explanation: detail.explanation || detail.message || "VALUES ARE IN CAD/LITRES",
          hint: detail.hint || "",
          sample: detail.sample_mismatch_rows || [],
        });
      } else {
        toast.error(detail?.message || detail || "Import failed");
      }
    }
    finally { setBusy(false); }
  };

  // Phase 2.A — drives the JobProgress component in the import modal.
  // On terminal state we refresh the underlying lists and show a final
  // toast with the counters.
  const { job: liveImportJob } = useJobPolling(importJob?.job_id, {
    onTerminal: (j) => {
      if (j.status === "completed") {
        const c = j.counters || {};
        const msg = `Imported ${c.inserted_count ?? "—"} · duplicates ${c.duplicate_count ?? 0}` +
          ` · failed ${c.failed_rows_count ?? 0} · unmatched ${c.unmatched_count ?? 0}`;
        const diffs = reconcileImport(expectedRef.current, {
          inserted: c.inserted_count, duplicates: c.duplicate_count, failed: c.failed_rows_count,
        });
        expectedRef.current = null;
        setImportDone({ status: "completed", inserted: c.inserted_count, duplicates: c.duplicate_count ?? 0,
                        failed: c.failed_rows_count ?? 0, diffs });
        if (diffs.length) toast.error(`Import result differs from validation: ${diffs.join("; ")}`, { duration: 20000 });
        else if ((c.failed_rows_count ?? 0) > 0) toast.warning(`Completed with failures · ${msg}`, { duration: 15000 });
        else toast.success(msg);
      } else if (j.status === "failed") {
        toast.error(`Import failed: ${j.error || "unknown error"}`);
      } else if (j.status === "cancelled") {
        toast.message("Import cancelled");
      }
      load();
      loadUploads();
    },
  });

  const deleteUpload = async (u) => {
    try {
      // Send the typed phrase as `confirm=...` so the backend's
      // destructive guard accepts the call (safety.py accepts both
      // "DELETE" and "I UNDERSTAND THIS IS IRREVERSIBLE"). Without
      // this the request returns 409 "Refusing destructive action".
      const params = new URLSearchParams({
        cascade: "true",
        confirm: REQUIRED_UPLOAD_DELETE_PHRASE,
      });
      const { data } = await api.delete(`/transactions/uploads/${u.upload_id}?${params}`);
      toast.success(`Permanently deleted "${u.filename}" · ${data.transactions_deleted} transactions removed`);
      setConfirmDelete(null);
      setConfirmDeleteText("");
      load(); loadUploads();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      toast.error(
        detail
          ? `${status ? `[${status}] ` : ""}${detail}`
          : `Delete failed${status ? ` (${status})` : ""}`,
      );
    }
  };

  const runReimportAudit = async () => {
    const { file, network: net, country: ctry, dry_run } = auditModal;
    if (!file) { toast.error("Pick a source CSV first."); return; }
    setAuditModal((s) => ({ ...s, busy: true, result: null }));
    try {
      const form = new FormData();
      form.append("network", net);
      form.append("country", ctry);
      form.append("dry_run", dry_run ? "true" : "false");
      form.append("file", file);
      const { data } = await api.post("/transactions/reimport-audit", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAuditModal((s) => ({ ...s, busy: false, result: data }));
      const verb = dry_run ? "Would recover" : "Recovered";
      if (data.inserted > 0) {
        toast.success(`${verb} ${data.inserted} missing rows · ${data.skipped_already_in_db} already in DB.`);
      } else {
        toast.info(`No missing rows — ${data.skipped_already_in_db} already in DB${data.skipped_unmatched_card ? `, ${data.skipped_unmatched_card} unmatched cards` : ""}.`);
      }
      if (!dry_run && data.inserted > 0) { load(); loadUploads(); }
    } catch (e) {
      setAuditModal((s) => ({ ...s, busy: false }));
      toast.error(e.response?.data?.detail || "Re-import audit failed");
    }
  };



  const clearFlag = async (t) => {
    try {
      await api.post(`/transactions/${t.transaction_id}/unflag`);
      toast.success(`Flag cleared on ${formatCard(t.card_number, t.network, t.card_last4)} ${t.tran_date}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to clear flag"); }
  };

  // Manual status transition. Used by the per-row "..." → status modal. The
  // backend guards against illegal transitions (e.g. pending without a
  // customer) so we just surface errors via toast.
  const openStatusEdit = (t) => {
    setStatusEdit({
      tx: t,
      status: t.status === "invoiced" ? "pending" : t.status,
      customer_id: t.customer_id || "",
      busy: false,
    });
  };
  const saveStatusEdit = async () => {
    if (!statusEdit) return;
    setStatusEdit((s) => ({ ...s, busy: true }));
    const body = { status: statusEdit.status };
    // Only send customer_id if it changed or if required by the new status.
    const originalCid = statusEdit.tx.customer_id || "";
    if (statusEdit.customer_id !== originalCid) {
      body.customer_id = statusEdit.customer_id; // "" → un-assign
    }
    try {
      const { data } = await api.patch(`/transactions/${statusEdit.tx.transaction_id}/status`, body);
      const bits = [`Status → ${data.status}`];
      if (data.invoice_recomputed) bits.push("invoice recomputed");
      if (data.invoice_deleted) bits.push("invoice removed (was only line)");
      toast.success(bits.join(" · "));
      setStatusEdit(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
      setStatusEdit((s) => ({ ...s, busy: false }));
    }
  };

  // Delete a single row. Invoiced rows require an explicit `force=true` flag
  // (handled by the confirm-modal checkbox); the backend recomputes invoice
  // totals on force.
  const runDeleteTx = async () => {
    if (!delTx) return;
    setDelTx((d) => ({ ...d, busy: true }));
    try {
      const params = delTx.force ? { force: true } : {};
      const { data } = await api.delete(`/transactions/${delTx.tx.transaction_id}`, { params });
      const bits = [
        `${data.mode === "soft" ? "Soft-deleted" : "Deleted"} transaction`,
      ];
      if (data.invoice_recomputed) bits.push("invoice recomputed");
      if (data.invoice_deleted) bits.push("invoice removed (was only line)");
      toast.success(bits.join(" · "));
      if (data.mode === "soft" && data.deleted_request_id) {
        setUndoState({
          kind: "transactions",
          deletedRequestId: data.deleted_request_id,
          count: 1,
          onRestored: () => { load(); loadUploads(); },
        });
      }
      setDelTx(null);
      load(); loadUploads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
      setDelTx((d) => ({ ...d, busy: false }));
    }
  };

  const [repriceJob, setRepriceJob] = useState(null);

  const reprice = async () => {
    setRepricing(true);
    try {
      // Phase 2.A — async reprice. Returns 202 + job_id; poll for progress.
      const { data } = await api.post("/transactions/reprice-fix-async");
      setRepriceJob({ job_id: data.job_id });
      toast.success(`Reprice queued · job ${data.job_id.slice(-6)}`);
    } catch (e) {
      // Fallback to legacy sync route.
      try {
        const { data } = await api.post("/transactions/reprice-flagged");
        const msg = `Fixed ${data.fixed}` + (data.still_flagged ? ` · ${data.still_flagged} still need review` : "");
        if (data.fixed > 0) toast.success(msg); else toast.info(msg);
        load();
      } catch (e2) {
        toast.error(e2.response?.data?.detail || e.response?.data?.detail || "Reprice failed");
      }
    } finally { setRepricing(false); }
  };

  const { job: liveRepriceJob } = useJobPolling(repriceJob?.job_id, {
    onTerminal: (j) => {
      if (j.status === "completed") {
        const c = j.counters || {};
        const fixed = c.fixed_count ?? 0;
        const still = c.still_flagged ?? 0;
        toast.success(`Reprice done · fixed ${fixed}` + (still ? ` · ${still} still need review` : ""));
      } else if (j.status === "failed") {
        toast.error(`Reprice failed: ${j.error || "unknown error"}`);
      }
      load();
    },
  });

  // One-click bulk-purge of every row currently flagged status=duplicate.
  // Defaults to SOFT-delete (May 2026): rows get a `deleted_request_id`
  // that powers the undo banner + Deleted Records page. Backend filters
  // out invoiced rows automatically (duplicates never get an invoice_id).
  //
  // SAFETY HARDENING (2026-05-29 — Cause #2 fix): we now FETCH the exact
  // blast radius (`transaction_count` + per-customer breakdown) from
  // `/transactions/duplicates/preview` BEFORE showing the modal. The
  // operator must type `DELETE N DUPLICATES` (where N is the live count)
  // before the confirm button enables. The endpoint also requires an
  // explicit `scope` param now, so a malformed call surfaces a 422
  // instead of silently nuking the entire system.
  const purgeDuplicates = async () => {
    const scope = filters.customer_id ? "customer" : "all";
    try {
      const params = { scope };
      if (scope === "customer") params.customer_id = filters.customer_id;
      const { data: preview } = await api.get(
        "/transactions/duplicates/preview", { params },
      );
      if (!preview.transaction_count) {
        toast.info("No duplicate transactions to delete in this scope.");
        return;
      }
      setPurgeModal({
        open: true,
        scope,
        customer_id: filters.customer_id || null,
        preview,
        typed: "",
        busy: false,
      });
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      toast.error(
        detail
          ? `${status ? `[${status}] ` : ""}${detail}`
          : `Failed to preview duplicates${status ? ` (${status})` : ""}`,
      );
    }
  };

  // Executes the purge after the modal's typed confirmation passes.
  const executePurgeFromModal = async () => {
    if (!purgeModal.preview) return;
    setPurgeModal((s) => ({ ...s, busy: true }));
    try {
      const params = {
        confirm: "DELETE",                       // safety.py guard
        scope: purgeModal.scope,                 // explicit blast radius
      };
      if (purgeModal.scope === "customer") {
        params.customer_id = purgeModal.customer_id;
      } else {
        params.all_customers_acknowledged = "true";
      }
      const { data } = await api.delete(
        "/transactions/duplicates", { params },
      );
      const drid = data.deleted_request_id;
      toast.success(
        `Soft-deleted ${data.deleted} duplicate transaction(s).` +
        (drid ? ` · request ${drid}` : ""),
      );
      if (data.mode === "soft" && drid) {
        setUndoState({
          kind: "transactions",
          deletedRequestId: drid,
          count: data.deleted,
          onRestored: () => load(),
        });
      }
      setPurgeModal({ open: false, scope: null, customer_id: null,
                      preview: null, typed: "", busy: false });
      load();
      loadLastPurgeTile();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      toast.error(
        detail
          ? `${status ? `[${status}] ` : ""}${detail}`
          : `Failed to delete duplicates${status ? ` (${status})` : ""}`,
      );
      setPurgeModal((s) => ({ ...s, busy: false }));
    }
  };

  // Refresh the "Undo last duplicate purge" tile shown in the page
  // header. Tile is hidden when no recent purge exists OR the last
  // purge is no longer restorable (rows were re-purged / hard-deleted
  // / retention window expired).
  const loadLastPurgeTile = async () => {
    try {
      const { data } = await api.get("/transactions/duplicates/last-purge");
      if (data?.available && data?.restorable) {
        setLastPurge(data);
      } else {
        setLastPurge(null);
      }
    } catch {
      setLastPurge(null);
    }
  };

  // One-click undo for the most recent duplicate-purge batch.
  const undoLastPurge = async () => {
    const ok = window.confirm(
      `Restore ${lastPurge.restorable_count} duplicate transaction(s) ` +
      `soft-deleted at ${lastPurge.created_at} by ${lastPurge.user_email}?`,
    );
    if (!ok) return;
    try {
      const { data } = await api.post(
        "/transactions/duplicates/undo-last",
      );
      if (data.restored > 0) {
        toast.success(`Restored ${data.restored} duplicate transaction(s).`);
      } else {
        toast.info(`Nothing to restore (${data.reason}).`);
      }
      setLastPurge(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || "Failed to undo last duplicate purge.");
    }
  };

  // Scan for SUSPECTED duplicates already in the DB (not just on re-upload).
  // Catches pre-existing dupes from before the dedupe-on-import feature
  // shipped, OR cases where the reference number got mangled in one of the
  // imports. Backend pairs are pre-grouped; we pre-tick all-but-the-oldest
  // row in each group as the "to demote" set, which the operator can edit.
  const scanForDuplicates = async () => {
    try {
      const params = filters.customer_id ? { customer_id: filters.customer_id } : {};
      const { data } = await api.get("/transactions/scan-duplicates", { params });
      if (data.group_count === 0) {
        toast.success("No suspected duplicates found in the current scope.");
        return;
      }
      const selected = new Set();
      for (const g of data.groups) {
        // Skip the first (oldest) row of each group — keep it as the original.
        for (let i = 1; i < g.rows.length; i++) selected.add(g.rows[i].transaction_id);
      }
      setScanDupes({ groups: data.groups, selected, busy: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    }
  };

  const toggleScanSelect = (txId) => {
    setScanDupes((s) => {
      const next = new Set(s.selected);
      next.has(txId) ? next.delete(txId) : next.add(txId);
      return { ...s, selected: next };
    });
  };

  const applyScanMark = async () => {
    if (!scanDupes || scanDupes.selected.size === 0) return;
    setScanDupes((s) => ({ ...s, busy: true }));
    try {
      const { data } = await api.post("/transactions/mark-duplicates", {
        transaction_ids: Array.from(scanDupes.selected),
      });
      toast.success(`Marked ${data.marked} row(s) as duplicate.`);
      setScanDupes(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark duplicates");
      setScanDupes((s) => ({ ...s, busy: false }));
    }
  };

  // ---- Bulk delete --------------------------------------------------------
  // Open the confirmation modal. `mode = "filtered"` uses the live sidebar
  // filters; `mode = "all"` ignores filters and targets every transaction.
  // We always run a dry-run first so the modal shows the exact counts the
  // server will act on (avoids mismatch between client-side row count and
  // server-side query).
  const openBulkDelete = async (mode) => {
    if (mode === "filtered" && bulkDeleteBlockReason()) { toast.error(bulkDeleteBlockReason()); return; }
    const payload =
      mode === "filtered"
        ? { ...filterPayload(), force: false, dry_run: true }
        : { force: false, dry_run: true };
    try {
      const url = mode === "filtered" ? "/transactions/bulk-delete" : "/transactions/reset-all";
      const { data } = await api.post(url, payload);
      setBulkDelete({ mode, preview: data, force: false, confirmText: "", busy: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not preview delete");
    }
  };

  // Re-run the dry-run when the user toggles the "include invoiced" checkbox
  // so the displayed counts always match the actual force state.
  const refreshBulkPreview = async (force) => {
    if (!bulkDelete) return;
    if (bulkDelete.mode === "filtered" && bulkDeleteBlockReason()) { toast.error(bulkDeleteBlockReason()); return; }
    const payload =
      bulkDelete.mode === "filtered"
        ? { ...filterPayload(), force, dry_run: true }
        : { force, dry_run: true };
    const url = bulkDelete.mode === "filtered" ? "/transactions/bulk-delete" : "/transactions/reset-all";
    try {
      const { data } = await api.post(url, payload);
      setBulkDelete((b) => ({ ...b, force, preview: data, confirmText: "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not refresh preview");
    }
  };

  const runBulkDelete = async () => {
    if (!bulkDelete) return;
    const REQUIRED = "I UNDERSTAND THIS IS IRREVERSIBLE";
    if (bulkDelete.confirmText !== REQUIRED) {
      toast.error(`Type the full phrase: ${REQUIRED}`);
      return;
    }
    if (bulkDelete.mode === "filtered" && bulkDeleteBlockReason()) { toast.error(bulkDeleteBlockReason()); return; }
    setBulkDelete((b) => ({ ...b, busy: true }));
    const url = bulkDelete.mode === "filtered" ? "/transactions/bulk-delete" : "/transactions/reset-all";
    const payload =
      bulkDelete.mode === "filtered"
        ? { ...filterPayload(), force: bulkDelete.force, confirm: REQUIRED }
        : { force: bulkDelete.force, confirm: REQUIRED };
    try {
      const { data } = await api.post(url, payload);
      const mode = data.mode || "hard";
      const parts = [
        `${mode === "soft" ? "Soft-deleted" : "Deleted"} ${data.deleted} transactions`,
      ];
      if (data.invoiced_skipped) parts.push(`${data.invoiced_skipped} invoiced rows skipped`);
      if (data.invoices_recomputed) parts.push(`${data.invoices_recomputed} invoice(s) recomputed`);
      if (data.invoices_deleted) parts.push(`${data.invoices_deleted} invoice(s) removed`);
      toast.success(parts.join(" · "));
      // Surface the undo banner — preloads deleted_request_id so the
      // operator can one-click revert within 30s.
      if (mode === "soft" && data.deleted_request_id && data.deleted > 0) {
        setUndoState({
          kind: "transactions",
          deletedRequestId: data.deleted_request_id,
          count: data.deleted,
          onRestored: () => { load(); loadUploads(); },
        });
      }
      setBulkDelete(null);
      load(); loadUploads();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      toast.error(
        detail
          ? `${status ? `[${status}] ` : ""}${detail}`
          : `Bulk delete failed${status ? ` (${status})` : ""}`,
      );
      setBulkDelete((b) => ({ ...b, busy: false }));
    }
  };

  // Mirror the sidebar filter state into the API payload (only the keys the
  // backend understands; empty strings are dropped).
  const filterPayload = () => {
    const out = {};
    for (const k of ["customer_id", "network", "status", "start", "end"]) {
      if (filters[k]) out[k] = filters[k];
    }
    // TRANSACTIONS-BULK-DELETE-COUNTRY-ISOLATION-IMPL-R1 (Feb 2026):
    // preserve `country` using the SAME network rule that `load()` uses
    // above, so Bulk Delete Filtered cannot cross country boundaries
    // (a PP+US delete must not also match PP-CA rows and vice-versa).
    // Country is meaningful only for lanes that carry a US/CA
    // distinction; kept for Network = All, omitted only for pipeline
    // (Canada-only by product definition). Symmetric to `load()`.
    if (filters.country && filters.network !== "pipeline") {
      out.country = filters.country;
    }
    return out;
  };
  // Fail closed: block Bulk Delete Filtered when the visible list uses a
  // filter the bulk-delete endpoint cannot apply.
  const bulkDeleteBlockReason = () => {
    const unsupported = ["province", "product", "unit", "upload_id"].filter((k) => filters[k]);
    if (unsupported.length) return `Bulk delete cannot apply these filters: ${unsupported.join(", ")}. Clear them first.`;
    if (filters.country && !["CA", "US"].includes(filters.country)) return `Unsupported country filter: ${filters.country}`;
    return null;
  };
  const hasAnyFilter = Object.values(filters).some((v) => v);

  const reportHref = (fmt) => {
    const params = { date_from: reportForm.date_from, date_to: reportForm.date_to };
    if (reportForm.customer_id) params.customer_id = reportForm.customer_id;
    if (reportForm.network) params.network = reportForm.network;
    if (reportForm.country) params.country = reportForm.country;
    return backendDownloadUrl(`/transactions/report/${fmt}`, params);
  };
  const triggerDownload = async (fmt) => {
    const params = { date_from: reportForm.date_from, date_to: reportForm.date_to };
    if (reportForm.customer_id) params.customer_id = reportForm.customer_id;
    if (reportForm.network) params.network = reportForm.network;
    if (reportForm.country) params.country = reportForm.country;
    try {
      const filename = await downloadFromBackend(`/transactions/report/${fmt}`, params);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to download ${fmt.toUpperCase()}`);
    }
  };

  // ------------------------------------------------------------------
  // Customer-safe download (Phase 2.4). Always sends `customer_id` +
  // `for_customer=true` so the server-side projection excludes margin /
  // GP / supplier / status columns. Backend also force-scopes for any
  // caller lacking `transactions.download_all_customers`, but doing it
  // here too keeps the UI honest.
  // ------------------------------------------------------------------
  const triggerCustomerReport = async () => {
    const {
      date_from, date_to, customer_ids, network, country, file_type,
    } = customerReportForm;
    if (!customer_ids || customer_ids.length === 0) {
      toast.error("Select at least one customer");
      return;
    }
    if (!date_from || !date_to) {
      toast.error("Pick a date range");
      return;
    }
    try {
      // Single customer → one file directly. Multi-customer → download
      // one file per customer sequentially so the browser prompts each.
      // (True server-side multi-cust ZIP would need a new endpoint; we
      // deliberately don't combine into one file per spec.)
      for (const cid of customer_ids) {
        const params = {
          date_from, date_to, customer_id: cid, for_customer: true,
        };
        // P4-N5B Slice C2 — canonical wire construction via the C1
        // adapter. Emits ONLY {esso, pipeline, petro_pass} + {CA, US, ""}
        // — NEVER `ta`, `ta_petro`, or any compound wire value. Backend
        // widening (12 call sites) remains the sole authority for the
        // `esso → {esso, ta_petro}` union.
        const wire = toWireParams({ network, country });
        if (wire.network) params.network = wire.network;
        if (wire.country) params.country = wire.country;
        const filename = await downloadFromBackend(
          `/transactions/report/${file_type}`, params,
        );
        toast.success(`Downloaded ${filename}`);
      }
      setShowCustomerReport(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const totalsByLane = laneTotals(rows);

  const openUploads = uploads.length;
  const flaggedCount = rows.filter((r) => r.status === "flagged" || r.status === "unmatched").length;
  const duplicateCount = rows.filter((r) => r.status === "duplicate").length;

  return (
    <SectionPad>
      <PageHeader
        title="Transactions"
        subtitle="OPERATIONS / LEDGER"
        testId="transactions-header"
        actions={
          <>
            <button className="gf-btn gf-btn-outline" onClick={() => setShowFlagHelp(true)} data-testid="status-guide-button" title="Status &amp; row colour guide for new users">
              <Info size={14}/> Status guide
            </button>
            <button className="gf-btn gf-btn-outline" onClick={() => setShowUploads(true)} data-testid="show-tx-uploads-button">
              Uploads {openUploads > 0 && <span className="ml-1 text-xs text-zinc-400">({openUploads})</span>}
            </button>
            <Can perm="transactions.edit">
            <button className="gf-btn gf-btn-outline" onClick={reprice} disabled={repricing || (liveRepriceJob && !["completed","failed","cancelled"].includes(liveRepriceJob.status))} data-testid="reprice-flagged-button" title="Re-run pricing on EVERY transaction (flagged + pending + invoiced). Use this after editing margin rules, uploading new price lists, or changing fuel-tax rates so existing rows pick up the new prices.">
              {repricing ? "Submitting…" : (liveRepriceJob && !["completed","failed","cancelled"].includes(liveRepriceJob.status) ? `Repricing… ${liveRepriceJob.progress_percent || 0}%` : "Re-apply margins")}
            </button>
            </Can>
            <Can perm="transactions.manage_duplicates">
            <button
              className="gf-btn gf-btn-outline"
              onClick={scanForDuplicates}
              data-testid="scan-duplicates-button"
              title="Scan all transactions to find rows that look like duplicates of each other (matched by invoice ref or by row fingerprint). Operator reviews and marks before anything changes."
            >
              <Funnel size={14}/> Scan duplicates
            </button>
            <button
              className="gf-btn gf-btn-outline text-orange-700 border-orange-300 hover:bg-orange-50"
              onClick={purgeDuplicates}
              disabled={duplicateCount === 0}
              data-testid="purge-duplicates-button"
              title="One-click delete every transaction currently flagged as duplicate (originals untouched)."
            >
              <Trash size={14}/> Delete duplicates{duplicateCount > 0 && <span className="ml-1 text-xs">({duplicateCount})</span>}
            </button>
            {lastPurge && (
              <button
                className="gf-btn gf-btn-outline text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                onClick={undoLastPurge}
                data-testid="undo-last-purge-button"
                title={`Restore the ${lastPurge.restorable_count} duplicate transaction(s) soft-deleted at ${lastPurge.created_at} by ${lastPurge.user_email}`}
              >
                Undo last purge
                <span className="ml-1 text-xs">({lastPurge.restorable_count})</span>
              </button>
            )}
            </Can>
            <Can perm="transactions.delete">
            <button
              className="gf-btn gf-btn-outline text-red-700 border-red-300 hover:bg-red-50"
              onClick={() => openBulkDelete("filtered")}
              disabled={!hasAnyFilter || rows.length === 0}
              data-testid="bulk-delete-shown-button"
              title={hasAnyFilter
                ? `Delete the ${rows.length} transaction(s) currently visible (matching filters)`
                : "Set at least one filter to enable bulk delete"}
            >
              <Trash size={14}/> Delete shown
            </button>
            </Can>
            <Can perm="transactions.download_customer_reports">
            <button className="gf-btn gf-btn-outline" onClick={() => setShowCustomerReport(true)}
                    data-testid="open-tx-customer-report-button"
                    title="Download customer-safe transaction report for one selected customer. Margins, GP and supplier costs are excluded.">
              <DownloadSimple size={14}/> Download Customer Report
            </button>
            </Can>
            <Can perm="transactions.download_all_customers">
            <button className="gf-btn gf-btn-outline text-orange-700 border-orange-300 hover:bg-orange-50"
                    onClick={() => setShowReport(true)} data-testid="open-tx-report-button"
                    title="Internal report across all customers. Includes margins, GP, supplier cost and status.">
              <DownloadSimple size={14}/> Internal All-Customers Report
            </button>
            </Can>
            <Can perm="transactions.upload">
            <button className="gf-btn gf-btn-outline" onClick={() => setShowBulkImport(true)} data-testid="bulk-import-tx-button" title="Import many transaction files at once">
              <UploadSimple size={14}/> Bulk import
            </button>
            <button className="gf-btn gf-btn-primary" onClick={() => setShowImport(true)} data-testid="import-tx-button"><UploadSimple size={14}/> Import</button>
            </Can>
          </>
        }
      />

      {/* ---- INTEGRITY GUARDRAIL BANNER (May 2026) -------------------
          Surfaces uploads where the manifest of imported transaction_ids
          has drifted from the live transactions collection — usually
          either silent data loss (critical) or an unaudited soft-
          delete (warn). Empty unless something is actually wrong, so
          this banner is invisible in the happy path. */}
      {integrityProblems.length > 0 && (
        <div
          className="gf-card mb-4 border-l-4 border-red-500 bg-red-50"
          data-testid="upload-integrity-banner"
        >
          <div className="p-4 flex flex-col sm:flex-row sm:items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <Warning size={28} className="text-red-600 shrink-0 mt-1"/>
              <div>
                <div className="font-semibold text-red-800">
                  Upload integrity check — {integrityProblems.length} upload{integrityProblems.length > 1 ? "s" : ""} need{integrityProblems.length === 1 ? "s" : ""} attention
                </div>
                <div className="text-xs text-red-700 mt-1">
                  These imports have transactions that have disappeared from the database without an audited delete request. Verify each before invoicing.
                </div>
                <ul
                  className="mt-2 text-xs text-red-900 space-y-1"
                  data-testid="upload-integrity-list"
                >
                  {integrityProblems.slice(0, 5).map((u) => (
                    <li
                      key={u.upload_id}
                      data-testid={`upload-integrity-row-${u.upload_id}`}
                      className="flex flex-wrap gap-x-3"
                    >
                      <span className={`font-semibold uppercase ${
                        u.severity === "critical" ? "text-red-700" : "text-orange-700"
                      }`}>
                        {u.severity}
                      </span>
                      <span className="font-mono">{u.filename}</span>
                      <span>
                        manifest={u.manifest_size} ·
                        alive={u.alive} ·
                        missing={u.missing}
                        {u.unaudited_soft_deletes > 0 && ` · unaudited-soft=${u.unaudited_soft_deletes}`}
                      </span>
                    </li>
                  ))}
                  {integrityProblems.length > 5 && (
                    <li className="text-zinc-600">…+{integrityProblems.length - 5} more</li>
                  )}
                </ul>
              </div>
            </div>
            <button
              className="gf-btn gf-btn-outline border-red-300 text-red-700 hover:bg-red-100 shrink-0"
              onClick={loadIntegrity}
              data-testid="upload-integrity-recheck-button"
              title="Re-run the integrity check against the live transactions collection"
            >
              Re-check
            </button>
          </div>
        </div>
      )}

      {/* ---- UPLOAD HISTORY TILE (May 2026) -------------------------
          Glance-view of import activity for admins: count of files,
          the latest filename + timestamp, and a one-click CTA into the
          full Uploads modal where individual files can be permanently
          deleted (typed-confirm gated). Helps operators spot stale or
          mistakenly-uploaded files without hunting through the toolbar. */}
      <div
        className="gf-card mb-4 border-l-4 border-blue-500"
        data-testid="upload-history-tile"
      >
        <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <FileText size={28} className="text-blue-600 shrink-0 mt-1"/>
            <div>
              <div className="font-semibold flex items-center gap-2">
                Transaction file uploads
                <span
                  className="text-xs text-zinc-500"
                  data-testid="upload-history-total"
                >({uploads.length})</span>
              </div>
              {uploads.length > 0 ? (
                <div className="text-xs text-zinc-600 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="flex items-center gap-1">
                    <Clock size={12}/>
                    <span data-testid="upload-history-last-time">
                      {uploads[0].created_at?.slice(0, 16).replace("T", " ")}
                    </span>
                    <span className="text-zinc-400">UTC</span>
                  </span>
                  <span className="font-mono truncate max-w-[420px]" title={uploads[0].filename}>
                    {uploads[0].filename}
                  </span>
                  <span className="text-zinc-400">·</span>
                  <span>{uploads[0].network?.toUpperCase()}</span>
                  <span className="text-zinc-400">·</span>
                  {/* R1.2 — split the aggregate row count into truthful
                      attempted / persisted / failed sub-counters so
                      operators can tell partial-successes from clean
                      uploads at a glance. Backwards-compatible: the
                      total-rows figure is still visible. */}
                  <span data-testid="upload-history-attempted">
                    {uploads[0].row_count} attempted
                  </span>
                  <span className="text-zinc-400">·</span>
                  <span data-testid="upload-history-persisted">
                    {uploads[0].imported ?? 0} persisted
                  </span>
                  {(uploads[0].failed_rows ?? 0) > 0 && (
                    <>
                      <span className="text-zinc-400">·</span>
                      <span
                        className="text-red-700 font-medium"
                        data-testid="upload-history-failed"
                      >
                        {uploads[0].failed_rows} failed
                      </span>
                    </>
                  )}
                </div>
              ) : (
                <div className="text-xs text-zinc-500 mt-1">
                  No transaction files have been uploaded yet.
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="gf-btn gf-btn-outline"
              onClick={() => setShowUploads(true)}
              data-testid="upload-history-open"
              disabled={uploads.length === 0}
              title="View every upload, inspect failed rows, and permanently delete files"
            >
              <FileText size={14}/> View &amp; manage
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <aside className="col-span-12 lg:col-span-3">
          <div className="gf-card">
            <div className="gf-card-header"><h3 className="flex items-center gap-2"><Funnel size={14}/> Filters</h3></div>
            <div className="gf-card-body space-y-3">
              <div><div className="overline mb-1">Customer</div>
                <select className="gf-input" value={filters.customer_id} onChange={(e) => setFilters({ ...filters, customer_id: e.target.value })} data-testid="filter-customer">
                  <option value="">All</option>
                  {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.company_name}</option>)}
                </select>
              </div>
              <div><div className="overline mb-1">Network</div>
                <select className="gf-input" value={filters.network} onChange={(e) => {
                  // P4-N5B Slice C5 R1 — canonical Network dropdown for
                  // the main Ledger Filters row. `filters.network` stores
                  // ONLY canonical providers ({"", "esso", "pipeline",
                  // "petro_pass"}). Country is reconciled via C1's
                  // capability helper; Pipeline is CA-only.
                  const nw = e.target.value;
                  const validCountries = _ledgerCountriesFor(nw);
                  const nextCountry = validCountries.includes(filters.country)
                    ? filters.country
                    : (validCountries.length === 1 ? validCountries[0] : "");
                  const provList = nw && PROVINCES_BY_NETWORK[nw] ? PROVINCES_BY_NETWORK[nw] : allProvinces;
                  const prodList = nw && PRODUCTS_BY_NETWORK[nw] ? PRODUCTS_BY_NETWORK[nw] : allProducts;
                  setFilters({
                    ...filters,
                    network: nw,
                    country: nextCountry,
                    province: provList.includes(filters.province) ? filters.province : "",
                    product:  prodList.includes(filters.product)  ? filters.product  : "",
                  });
                }} data-testid="filter-network">
                  <option value="">All networks</option>
                  <option value="esso">Esso</option>
                  <option value="pipeline">Pipeline</option>
                  <option value="petro_pass">Petro-Pass</option>
                </select>
              </div>
              <div data-testid="filter-country-wrap"><div className="overline mb-1">Country</div>
                <select
                  className="gf-input"
                  value={filters.country}
                  disabled={_ledgerCountriesFor(filters.network).length <= 1}
                  onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                  data-testid="filter-country"
                  title={filters.network === "pipeline" ? "Pipeline is Canada-only." : "Filter transactions by country. Backend widening on the Ledger renders Esso+USA supplier-safe (esso ∪ ta_petro / US)."}
                >
                  {/* P4-N5B Slice C5 R1 — Country options driven ENTIRELY
                      by the C1 capability matrix. Pipeline is
                      single-capability (CA-only) and disables the
                      selector. `toWireParams` wire path stays safe on
                      the Ledger because backend at transactions.py:2911
                      widens `esso → {esso, ta_petro}`. */}
                  {_ledgerCountriesFor(filters.network).map((c) => (
                    <option key={c || "all"} value={c}>{COUNTRY_LABELS[c]}</option>
                  ))}
                </select>
              </div>
              <div><div className="overline mb-1">Province</div>
                <select
                  className="gf-input"
                  value={filters.province}
                  onChange={(e) => setFilters({ ...filters, province: e.target.value })}
                  data-testid="filter-province"
                  title="Provinces follow the operator's published per-network spec (Esso: AB/BC/MB/NS/ON/QC/SK · Pipeline: AB/BC/NB/NL/NS/ON/QC)."
                >
                  <option value="">All</option>
                  {(filters.network && PROVINCES_BY_NETWORK[filters.network]
                    ? PROVINCES_BY_NETWORK[filters.network]
                    : allProvinces
                  ).map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div><div className="overline mb-1">Product</div>
                <select
                  className="gf-input"
                  value={filters.product}
                  onChange={(e) => setFilters({ ...filters, product: e.target.value })}
                  data-testid="filter-product"                  title="Products use canonical labels. 'Diesel LS' covers raw 'ULSD' / 'DIESEL' / 'Diesel'. 'DEFD' covers raw 'DEF'. RFR is Esso-only."
                >
                  <option value="">All</option>
                  {(filters.network && PRODUCTS_BY_NETWORK[filters.network]
                    ? PRODUCTS_BY_NETWORK[filters.network]
                    : allProducts
                  ).map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              {/* P4-N2D (2026-02) — Admin Unit filter.
                  Only visible when a specific customer is selected
                  (the endpoint is customer-scoped by definition).
                  Options come from `/api/admin/units`, i.e. the
                  customer's stored historical `transaction.unit`
                  values — NOT current card ownership. So historical
                  units on transferred cards (e.g. JSONS Esso
                  NR1001/NR1002 after transfer to Night Road) remain
                  visible under the historical customer. */}
              {filters.customer_id && (
                <div data-testid="filter-unit-wrap">
                  <div className="overline mb-1">Unit</div>
                  <select
                    className="gf-input"
                    value={filters.unit}
                    onChange={(e) => setFilters({ ...filters, unit: e.target.value })}
                    data-testid="filter-unit"
                    title="Historical transaction unit values for the selected customer. Includes units from transferred cards so pre-transfer rows remain filterable under the historical customer."
                  >
                    <option value="">All units</option>
                    {unitOptions.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                </div>
              )}
              <div><div className="overline mb-1 flex items-center gap-1">Status
                <button type="button" onClick={() => setShowFlagHelp(true)} title="What do these statuses mean?" data-testid="flag-help-button" className="text-zinc-400 hover:text-zinc-700">
                  <Info size={12}/>
                </button>
              </div>
                <select className="gf-input" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status">
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="flagged">Flagged</option>
                  <option value="unmatched">Unmatched</option>
                  <option value="invoiced">Invoiced</option>
                  <option value="duplicate">Duplicate</option>
                </select>
              </div>
              <div><div className="overline mb-1">From</div><input type="date" className="gf-input" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} /></div>
              <div><div className="overline mb-1">To</div><input type="date" className="gf-input" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} /></div>
              <button className="gf-btn gf-btn-outline w-full" onClick={() => setFilters({ customer_id: "", network: "", country: "", status: "", start: "", end: "", upload_id: "" })}>Reset filters</button>
            </div>
            {filters.upload_id && (
              <div className="mt-3 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 px-2 py-1 text-xs" data-testid="upload-filter-pill">
                <span>Filtering by upload <span className="font-mono">{filters.upload_id}</span></span>
                <button
                  className="text-blue-700 hover:underline"
                  onClick={() => setFilters({ ...filters, upload_id: "" })}
                  data-testid="clear-upload-filter"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          <div className="mt-4 space-y-2" data-testid="totals-summary" data-lane={txLane}>
            {txLane !== "MIXED" ? (
              <LaneSummary lane={txLane} t={totalsByLane[txLane] || { qty: 0, amt: 0, gp: 0 }} prefix="totals" />
            ) : (
              <>
                <div className="text-[11px] text-zinc-500" data-testid="totals-mixed-note">Mixed countries — totals shown separately. Select a country for a single-currency summary.</div>
                {["CA", "US"].filter((l) => totalsByLane[l]).map((l) => (
                  <div key={l} data-testid={`totals-lane-${l}`}>
                    <div className="overline mb-1">{LANE_NAME[l]}</div>
                    <LaneSummary lane={l} t={totalsByLane[l]} prefix={`totals-${l}`} />
                  </div>
                ))}
              </>
            )}
            {rows.length >= 2000 && (
              <div className="text-[11px] text-amber-700" data-testid="totals-cap-note">Totals cover the first {rows.length.toLocaleString()} loaded rows only — narrow the filters for complete totals.</div>
            )}
          </div>
          {flaggedCount > 0 && (
            <div className="mt-4 p-3 border border-yellow-300 bg-yellow-50 text-xs">
              <div className="font-semibold text-yellow-900 mb-1">{flaggedCount} row(s) need review</div>
              <div className="text-yellow-800">Click the <CheckCircle size={12} style={{display:"inline"}}/> icon on any flagged row to clear it after review.</div>
            </div>
          )}
        </aside>

        <div className="col-span-12 lg:col-span-9">
          <div className="gf-card">
            <div className="gf-card-header">
              <h3>{rows.length} transactions</h3>
              <div className="overline">SUPPLIER BASE + MARGIN = NET PRICE · NET PRICE + PFT + LOCAL = UNIT PRICE</div>
            </div>
            <StickyHScroll testId="tx-table-hscroll">
              <table className="gf-table" data-testid="tx-table" data-lane={txLane}>
                <thead>
                  <tr>
                    <th>DATE</th><th>CUSTOMER</th><th>CARD</th><th>UNIT #</th><th>STATION</th><th>PROD</th>
                    <th className="text-right" data-testid="tx-col-qty">QTY{colLabel(txLane, "qty")}</th>
                    <th className="text-right" data-testid="tx-col-net-price">NET PRICE{colLabel(txLane, "rate")}</th>
                    <th className="text-right" data-testid="tx-col-mrg">MARGIN{colLabel(txLane, "mrg")}</th>
                    <th className="text-right" data-testid="tx-col-pft">PFT{colLabel(txLane, "rate")}</th>
                    <th className="text-right" data-testid="tx-col-local">LOCAL{colLabel(txLane, "rate")}</th>
                    <th className="text-right" data-testid="tx-col-unit-price">UNIT PRICE{colLabel(txLane, "rate")}</th>
                    <th className="text-right" data-testid="tx-col-amt">AMT{colLabel(txLane, "amt")}</th>
                    <th className="text-right">GST/HST</th>
                    <th className="text-right">PST/QST</th>
                    <th className="text-right">FINAL AMT</th>
                    <th className="text-right" data-testid="tx-col-gp">GP{colLabel(txLane, "amt")}</th>
                    <th>STATUS</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.transaction_id} style={t.status === "flagged" ? { background: "#FEFCE8" } : t.status === "unmatched" ? { background: "#FEE2E2" } : t.status === "duplicate" ? { background: "#FFEDD5" } : {}}>
                      <td className="font-mono text-xs">{t.tran_date}</td>
                      <td className="text-xs">{t.customer_name || <span className="text-zinc-400">—</span>}</td>
                      <td className="font-mono" data-testid={`tx-card-${t.transaction_id}`}>{formatCard(t.card_number, t.network, t.card_last4)}</td>
                      <td className="font-mono text-xs">{t.unit || <span className="text-zinc-400">—</span>}</td>
                      <td className="text-xs">{t.station_name}<div className="text-[10px] text-zinc-400 font-mono">{t.city} {t.province}</div></td>
                      <td className="font-mono text-xs">{displayProduct(t.product, t.network)}</td>
                      <td className="gf-num">{t.qty?.toFixed(2)}<RowUnit lane={txLane} t={t} k="qty" /></td>
                      <td className="gf-num" data-testid={`tx-net-price-${t.transaction_id}`}>
                        {t.net_price_display?.toFixed(4)}<RowUnit lane={txLane} t={t} k="rate" />
                        {t.other_per_l_tax > 0 && (
                          <span title={`FET/PCT ${t.other_per_l_tax.toFixed(4)} per unit also deducted from NET PRICE (not shown as a column)`} className="ml-1 text-amber-600" data-testid={`tx-net-price-flag-${t.transaction_id}`}>⚑</span>
                        )}
                      </td>
                      <td className="gf-num">{t.margin_cpl?.toFixed(4)}<RowUnit lane={txLane} t={t} k="mrg" /></td>
                      <td className="gf-num" data-testid={`tx-pft-${t.transaction_id}`}>{t.pft_per_l?.toFixed(4)}<RowUnit lane={txLane} t={t} k="rate" /></td>
                      <td className="gf-num" data-testid={`tx-local-${t.transaction_id}`}>{t.local_per_l?.toFixed(4)}<RowUnit lane={txLane} t={t} k="rate" /></td>
                      <td className="gf-num font-semibold" data-testid={`tx-unit-price-${t.transaction_id}`}>{t.customer_cpl?.toFixed(4)}<RowUnit lane={txLane} t={t} k="rate" /></td>
                      <td className="gf-num">${t.customer_amount?.toFixed(2)}<RowUnit lane={txLane} t={t} k="amt" /></td>
                      <td className="gf-num" data-testid={`tx-tax-gst-${t.transaction_id}`}>
                        {t.tax_gst_hst_fnt != null ? `$${t.tax_gst_hst_fnt.toFixed(2)}` : "—"}
                      </td>
                      <td className="gf-num" data-testid={`tx-tax-pst-${t.transaction_id}`}>
                        {t.tax_pst_qst != null ? `$${t.tax_pst_qst.toFixed(2)}` : "—"}
                      </td>
                      <td className="gf-num font-semibold" data-testid={`tx-final-${t.transaction_id}`}>
                        {t.final_amount != null ? `$${t.final_amount.toFixed(2)}` : "—"}
                      </td>
                      <td className="gf-num" style={{ color: t.gross_profit > 0 ? "#16A34A" : "#71717a" }}>${t.gross_profit?.toFixed(2)}</td>
                      <td>
                        <Badge variant={t.status === "invoiced" ? "success" : t.status === "flagged" ? "warning" : t.status === "unmatched" ? "error" : t.status === "duplicate" ? "warning" : "info"}>{t.status}</Badge>
                        {t.notes && <div className="text-[10px] text-zinc-500 mt-1 max-w-[160px] truncate" title={t.notes}>{t.notes}</div>}
                        {t.cross_network_hint && (
                          <div className="text-[10px] mt-1 max-w-[180px] text-blue-700" title={`Same card # exists on ${t.cross_network_hint.other_network.toUpperCase()} for ${t.cross_network_hint.customer_name}`}>
                            <span className="font-mono">↪</span> {t.cross_network_hint.other_network.toUpperCase()}: <b className="truncate">{t.cross_network_hint.customer_name}</b>
                          </div>
                        )}
                      </td>
                      <td className="text-right">
                        {t.status === "flagged" && (
                          <button className="gf-btn gf-btn-ghost" title="Clear flag (mark reviewed)"
                            onClick={() => clearFlag(t)} data-testid={`unflag-${t.transaction_id}`}>
                            <CheckCircle size={14}/>
                          </button>
                        )}
                        <button
                          className="gf-btn gf-btn-ghost"
                          title="Change status / assign customer"
                          onClick={() => openStatusEdit(t)}
                          data-testid={`edit-status-${t.transaction_id}`}
                        >
                          <PencilSimple size={14}/>
                        </button>
                        <button
                          className="gf-btn gf-btn-ghost text-red-600 hover:text-red-800"
                          title={t.status === "invoiced"
                            ? "Delete (will recompute the invoice)"
                            : "Delete this transaction"}
                          onClick={() => setDelTx({ tx: t, force: t.status === "invoiced", busy: false })}
                          data-testid={`delete-tx-${t.transaction_id}`}
                        >
                          <Trash size={14}/>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </StickyHScroll>
          </div>
        </div>
      </div>

      {/* ---- IMPORT MODAL ---- */}
      {showImport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !busy && setShowImport(false)}>
          <div className={`bg-white w-full ${preflight ? "max-w-6xl" : "max-w-lg"} max-h-[92vh] overflow-auto border border-zinc-200`} onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>Import transactions</h3></div>
            <div className="gf-card-body space-y-3">
              <div><div className="overline mb-1">Network</div>
                <select className="gf-input" value={network} onChange={(e) => setNetwork(e.target.value)} data-testid="import-network">
                  <option value="esso">Esso (XLSX/CSV)</option>
                  <option value="pipeline">Pipeline (XLS)</option>
                  <option value="petro_pass">Petro-Pass (XLSX)</option>
                </select>
              </div>
              {network === "esso" && (
                <div data-testid="import-country-wrap">
                  <div className="overline mb-1">Country</div>
                  <select
                    className="gf-input"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    data-testid="import-country"
                  >
                    <option value="CA">Canada</option>
                    <option value="US">USA</option>
                  </select>
                  <div className="text-[11px] text-zinc-500 mt-1">
                    ESSO files have completely different layouts in USA vs Canada (tax columns differ). Pick the one matching the file you are uploading.
                  </div>
                </div>
              )}
              {network === "petro_pass" && (
                <div data-testid="import-petro-pass-country-wrap">
                  <div className="overline mb-1">Country</div>
                  <select
                    className="gf-input"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    data-testid="import-petro-pass-country"
                  >
                    <option value="CA">Petro-Pass Canada (CAD/L)</option>
                    <option value="US">Petro-Pass USA (USD/GAL)</option>
                  </select>
                  <div className="text-[11px] text-zinc-500 mt-1" data-testid="import-petro-pass-note">
                    Canada uses the <b>Purchases by Card per Page Including Taxes</b> (Report31) XLSX. USA uses the <b>U.S. Purchases by Card</b> (Report25) XLSX. The lanes are mutually exclusive — the file's signature validates the selected lane.
                  </div>
                </div>
              )}
              <div><div className="overline mb-1">Customer (optional — overrides card match)</div>
                <select className="gf-input" value={customer_id} onChange={(e) => setCustomerId(e.target.value)} data-testid="import-customer">
                  <option value="">Auto-match by card</option>
                  {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.company_name}</option>)}
                </select>
              </div>
              <div><div className="overline mb-1">File</div>
                <input ref={fileRef} type="file" className="gf-input" accept=".csv,.xls,.xlsx" data-testid="import-file-input" onChange={resetPreflight} disabled={!!liveImportJob && !["completed","failed","cancelled"].includes(liveImportJob.status)} />
              </div>
              <div className="flex items-center gap-2 text-xs">
                <label className={`flex items-center gap-1 ${workerOnline ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`} data-testid="import-async-toggle-label">
                  <input
                    type="checkbox"
                    checked={useAsync && workerOnline}
                    disabled={!workerOnline}
                    onChange={(e) => setUseAsync(e.target.checked)}
                    data-testid="import-async-toggle"
                  />
                  Background import (recommended for large files)
                </label>
                <span className="text-zinc-400">·</span>
                <span className="text-zinc-500">{useAsync && workerOnline ? "Uploads + processes in the worker" : "Legacy blocking path"}</span>
              </div>
              <WorkerOfflineNotice online={workerOnline} />
              {liveImportJob && (
                <div data-testid="import-job-progress-container">
                  <JobProgress job={liveImportJob}/>
                </div>
              )}
              <PreflightPanel preflight={preflight} ack={preflightAck} setAck={setPreflightAck} />
              {importDone && (
                <div className={`border p-2 text-xs ${importDone.diffs?.length ? "border-red-300 bg-red-50 text-red-700" : "border-emerald-300 bg-emerald-50 text-emerald-800"}`} data-testid="import-completed-result">
                  {importDone.status === "queued" ? "Import submitted — this validation has been used." : (
                    <>Import {importDone.failed > 0 ? "completed with failures" : "completed"} · inserted {importDone.inserted ?? "—"} · duplicates {importDone.duplicates} · failed {importDone.failed}
                      {importDone.diffs?.length ? ` · differs from validation: ${importDone.diffs.join("; ")}` : ""}</>
                  )}
                  <div className="mt-1 text-zinc-600">To import again, choose a file and Validate again.</div>
                </div>
              )}
              <div className="text-xs text-zinc-500" data-testid="import-duplicate-note">Validate first. Existing transactions are never overwritten or repriced — duplicates (same card + reference) are recorded as separate excluded duplicate rows. Unmatched cards are flagged for review.</div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => { setImportJob(null); setShowImport(false); }} disabled={busy} data-testid="cancel-import-modal">
                {liveImportJob && ["completed","failed","cancelled"].includes(liveImportJob.status) ? "Close" : "Cancel"}
              </button>
              <button
                className="gf-btn gf-btn-outline"
                onClick={runPreflight}
                disabled={busy || preflightBusy || (!!liveImportJob && !["completed","failed","cancelled"].includes(liveImportJob.status))}
                data-testid="validate-import-button"
              >
                {preflightBusy ? "Validating…" : (preflight ? "Re-validate" : "Validate")}
              </button>
              <button
                className="gf-btn gf-btn-primary"
                onClick={() => upload()}
                disabled={busy || !!importDone || !preflight || !preflight.can_import || (preflight.requires_confirmation && !preflightAck) || (!!liveImportJob && !["completed","failed","cancelled"].includes(liveImportJob.status))}
                data-testid="submit-import-button"
              >
                {busy ? "Submitting…" : (liveImportJob && !["completed","failed","cancelled"].includes(liveImportJob.status) ? "Processing…" : "Import")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- CURRENCY / UOM MISMATCH CONFIRM ---- */}
      {/* Operator RCA 2026-07-09: Esso USD/GAL upload with CAD/Liters
          rows requires an explicit "Upload anyway" confirmation. */}
      {currencyMismatchWarning && (
        <div
          className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
          data-testid="currency-mismatch-overlay"
        >
          <div className="bg-white w-full max-w-lg border-2 border-amber-500 shadow-2xl"
               onClick={(e) => e.stopPropagation()}
               data-testid="currency-mismatch-modal">
            <div className="p-4 border-b border-amber-200 bg-amber-50">
              <h3 className="text-lg font-semibold text-amber-900 tracking-tight"
                  data-testid="currency-mismatch-title">
                VALUES ARE IN CAD/LITRES
              </h3>
              <div className="text-xs text-amber-800 mt-1">
                File: <span className="font-mono">{currencyMismatchWarning.filename}</span>
              </div>
            </div>
            <div className="p-4 space-y-3 text-sm text-zinc-800">
              <p data-testid="currency-mismatch-explanation">
                {currencyMismatchWarning.explanation}
              </p>
              <div className="text-xs bg-zinc-50 border border-zinc-200 p-3">
                <div className="font-medium text-zinc-700 mb-1">Summary</div>
                <div>Total rows parsed: <b>{currencyMismatchWarning.rowsTotal}</b></div>
                <div>Rows tagged CAD/Liters: <b className="text-amber-900">{currencyMismatchWarning.rowsMismatched}</b></div>
              </div>
              {currencyMismatchWarning.sample?.length > 0 && (
                <div className="text-xs">
                  <div className="font-medium text-zinc-700 mb-1">First mismatched rows</div>
                  <table className="w-full border border-zinc-200">
                    <thead className="bg-zinc-100 text-zinc-600">
                      <tr>
                        <th className="p-1 text-left">Date</th>
                        <th className="p-1 text-left">Ref</th>
                        <th className="p-1 text-left">Product</th>
                        <th className="p-1 text-right">Qty</th>
                        <th className="p-1 text-left">UOM</th>
                        <th className="p-1 text-left">Ccy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currencyMismatchWarning.sample.map((r, i) => (
                        <tr key={i} className="border-t border-zinc-200">
                          <td className="p-1">{r.tran_date}</td>
                          <td className="p-1 font-mono">{r.reference}</td>
                          <td className="p-1">{r.product}</td>
                          <td className="p-1 text-right">{r.qty}</td>
                          <td className="p-1 text-amber-900 font-semibold">{r.uom}</td>
                          <td className="p-1 text-amber-900 font-semibold">{r.currency}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {currencyMismatchWarning.hint && (
                <p className="text-xs text-zinc-600 italic">{currencyMismatchWarning.hint}</p>
              )}
            </div>
            <div className="flex justify-end gap-2 p-3 border-t border-zinc-200 bg-zinc-50">
              <button
                className="gf-btn gf-btn-outline"
                onClick={() => setCurrencyMismatchWarning(null)}
                disabled={busy}
                data-testid="currency-mismatch-cancel"
              >
                Cancel upload
              </button>
              <button
                className="gf-btn gf-btn-primary bg-amber-600 hover:bg-amber-700"
                onClick={() => {
                  setCurrencyMismatchWarning(null);
                  upload({ forceCurrencyMismatch: true });
                }}
                disabled={busy}
                data-testid="currency-mismatch-force"
              >
                Upload anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- REPORT MODAL ---- */}
      {/* ---- CUSTOMER-SAFE REPORT MODAL (Phase 2.4) ---- */}
      {showCustomerReport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
             onClick={() => setShowCustomerReport(false)}
             data-testid="customer-report-modal">
          <div className="bg-white w-full max-w-lg border border-zinc-200 max-h-[90vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header">
              <h3>Download Customer Report</h3>
              <div className="text-xs text-zinc-500 mt-1">
                Customer-safe format. Margin, profit, supplier cost and
                internal pricing information are excluded.
              </div>
            </div>
            <div className="gf-card-body space-y-3">
              <div>
                <div className="overline mb-1">Customer <span className="text-red-500">*</span></div>
                <input
                  type="text"
                  className="gf-input w-full"
                  placeholder="Search customers…"
                  value={customerReportForm.search}
                  onChange={(e) => setCustomerReportForm({ ...customerReportForm, search: e.target.value })}
                  data-testid="customer-report-search"
                />
                <div className="mt-2 border border-zinc-200 rounded-sm max-h-48 overflow-y-auto"
                     data-testid="customer-report-list">
                  {reportPickerCustomers
                    .filter((c) => !customerReportForm.search ||
                      (c.company_name || "").toLowerCase().includes(customerReportForm.search.toLowerCase()))
                    .map((c) => {
                      const checked = customerReportForm.customer_ids.includes(c.customer_id);
                      return (
                        <label key={c.customer_id}
                               className={`flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-zinc-50 ${checked ? "bg-blue-50" : ""}`}
                               data-testid={`customer-report-option-${c.customer_id}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const ids = checked
                                ? customerReportForm.customer_ids.filter(x => x !== c.customer_id)
                                : [...customerReportForm.customer_ids, c.customer_id];
                              setCustomerReportForm({ ...customerReportForm, customer_ids: ids });
                            }}
                          />
                          <span className="flex-1">{c.company_name}</span>
                        </label>
                      );
                    })}
                  {reportPickerCustomers.length === 0 && (
                    <div className="p-3 text-sm text-zinc-400">No customers available.</div>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-1" data-testid="customer-report-count">
                  {customerReportForm.customer_ids.length} customer(s) selected
                  {customerReportForm.customer_ids.length > 1 && " — one separate file will be downloaded per customer"}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><div className="overline mb-1">Date from</div>
                  <input type="date" className="gf-input" value={customerReportForm.date_from}
                    onChange={(e) => setCustomerReportForm({ ...customerReportForm, date_from: e.target.value })}
                    data-testid="customer-report-from" />
                </div>
                <div><div className="overline mb-1">Date to</div>
                  <input type="date" className="gf-input" value={customerReportForm.date_to}
                    onChange={(e) => setCustomerReportForm({ ...customerReportForm, date_to: e.target.value })}
                    data-testid="customer-report-to" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="overline mb-1">Network</div>
                  <select className="gf-input" value={customerReportForm.network}
                          onChange={(e) => {
                            // P4-N5B Slice C2 — Network / Country
                            // reconciliation delegated to the C1 shared
                            // adapter (`networkCountryCatalog.js`).
                            //
                            // Rules consumed from C1:
                            //   All networks → All / CA / US
                            //   Esso         → All / CA / US
                            //   Pipeline     → CA only (locked)
                            //   Petro-Pass   → All / CA / US
                            //
                            // Emits canonical `network` values ONLY
                            // ({esso, pipeline, petro_pass}); NEVER `ta`,
                            // NEVER `ta_petro`. Backend widening is the
                            // authority for `esso → {esso, ta_petro}`.
                            const nextNet = e.target.value;
                            const validCountries = _customerReportCountriesFor(nextNet);
                            const nextCountry = validCountries.includes(customerReportForm.country)
                              ? customerReportForm.country
                              : validCountries[0];
                            setCustomerReportForm({ ...customerReportForm, network: nextNet, country: nextCountry });
                          }}
                          data-testid="customer-report-network">
                    <option value="">All networks</option>
                    <option value="esso">Esso</option>
                    <option value="pipeline">Pipeline</option>
                    <option value="petro_pass">Petro-Pass</option>
                  </select>
                </div>
                <div>
                  <div className="overline mb-1">Country</div>
                  <select className="gf-input" value={customerReportForm.country}
                          disabled={_customerReportCountriesFor(customerReportForm.network).length <= 1}
                          onChange={(e) => setCustomerReportForm({ ...customerReportForm, country: e.target.value })}
                          data-testid="customer-report-country">
                    {/* P4-N5B Slice C2 — Country options are geographic and
                        driven ENTIRELY by the C1 capability matrix. A
                        single-capability lane (Pipeline = CA) renders
                        exactly one option and disables the selector so
                        no impossible pair can leave the modal. */}
                    {_customerReportCountriesFor(customerReportForm.network).map((c) => (
                      <option key={c || "all"} value={c}>{COUNTRY_LABELS[c]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <div className="overline mb-1">File format</div>
                <div className="flex gap-2" data-testid="customer-report-format">
                  {["csv", "pdf"].map((f) => (
                    <button key={f} type="button"
                            className={`gf-btn text-xs ${customerReportForm.file_type === f ? "gf-btn-primary" : "gf-btn-outline"}`}
                            onClick={() => setCustomerReportForm({ ...customerReportForm, file_type: f })}
                            data-testid={`customer-report-format-${f}`}>
                      {f.toUpperCase()}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  CSV / PDF use the exact same customer-safe dataset (identical rows and headers as Admin → Customer Profile → Download Transaction Report).
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline"
                      onClick={() => setShowCustomerReport(false)}
                      data-testid="customer-report-cancel">Cancel</button>
              <button className="gf-btn gf-btn-primary"
                      onClick={triggerCustomerReport}
                      disabled={customerReportForm.customer_ids.length === 0}
                      data-testid="customer-report-download">
                <DownloadSimple size={14}/> Download
              </button>
            </div>
          </div>
        </div>
      )}

      {showReport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowReport(false)}>
          <div className="bg-white w-full max-w-lg border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>Download transaction report</h3></div>
            <div className="gf-card-body space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="overline mb-1">From</div>
                  <input type="date" className="gf-input" value={reportForm.date_from}
                    onChange={(e) => setReportForm({ ...reportForm, date_from: e.target.value })} data-testid="tx-report-from" />
                </div>
                <div><div className="overline mb-1">To</div>
                  <input type="date" className="gf-input" value={reportForm.date_to}
                    onChange={(e) => setReportForm({ ...reportForm, date_to: e.target.value })} data-testid="tx-report-to" />
                </div>
              </div>
              <div><div className="overline mb-1">Network</div>
                <select className="gf-input" value={reportForm.network} onChange={(e) => {
                  // P4-N5B Slice B — reconcile Country when Network capability changes.
                  // Transaction capability matrix: Esso CA+US · Pipeline CA · Petro-Pass CA+US.
                  // If the current Country is not supported by the newly chosen Network,
                  // reset Country to "" (All) so we never ship an impossible combination.
                  const nextNet = e.target.value;
                  const supported = nextNet === "pipeline" ? new Set(["CA"])
                                  : nextNet === "" ? new Set(["", "CA", "US"])
                                  : new Set(["CA", "US"]);
                  const nextCountry = supported.has(reportForm.country) ? reportForm.country
                                    : (nextNet === "pipeline" ? "CA" : "");
                  setReportForm({ ...reportForm, network: nextNet, country: nextCountry });
                }} data-testid="tx-report-network">
                  <option value="esso">Esso only</option>
                  <option value="pipeline">Pipeline only</option>
                  <option value="petro_pass">Petro-Pass only</option>
                  <option value="">All networks</option>
                </select>
              </div>
              <div><div className="overline mb-1">Country</div>
                <select className="gf-input" value={reportForm.country}
                  disabled={reportForm.network === "pipeline"}
                  onChange={(e) => setReportForm({ ...reportForm, country: e.target.value })}
                  data-testid="tx-report-country">
                  {/* Country options follow Network capability. Pipeline is CA-only so
                      the whole selector is disabled and pinned to Canada. */}
                  {reportForm.network === "pipeline" ? (
                    <option value="CA">Canada</option>
                  ) : (
                    <>
                      <option value="">All</option>
                      <option value="CA">Canada</option>
                      <option value="US">USA</option>
                    </>
                  )}
                </select>
              </div>
              <div><div className="overline mb-1">Customer</div>
                <select className="gf-input" value={reportForm.customer_id} onChange={(e) => setReportForm({ ...reportForm, customer_id: e.target.value })} data-testid="tx-report-customer">
                  <option value="">All customers</option>
                  {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.company_name}</option>)}
                </select>
              </div>
              <div className="text-xs text-zinc-500">CSV includes full pricing breakdown (rack, margin, customer CPL, GP). PDF is a condensed summary.</div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setShowReport(false)}>Close</button>
              <button className="gf-btn gf-btn-outline" onClick={() => triggerDownload("csv")} data-testid="tx-report-csv">CSV</button>
              <button className="gf-btn gf-btn-primary" onClick={() => triggerDownload("pdf")} data-testid="tx-report-pdf">PDF</button>
            </div>
          </div>
        </div>
      )}

      {/* ---- UPLOADS HISTORY MODAL ---- */}
      {showUploads && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowUploads(false)}>
          <div className="bg-white w-full max-w-4xl border border-zinc-200 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>Transaction uploads ({uploads.length})</h3></div>
            <div className="overflow-auto flex-1">
              <table className="gf-table">
                <thead>
                  <tr>
                    <th>UPLOADED</th><th>NETWORK</th><th>FILE</th><th>CUSTOMER</th><th>PERIOD</th>
                    <th className="text-right">ROWS</th><th className="text-right">NEW</th><th className="text-right">UPD</th>
                    <th className="text-right">FLAG</th><th className="text-right">UNMATCHED</th>
                    <th className="text-right">FAILED</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {uploads.map((u) => (
                    <tr key={u.upload_id}>
                      <td className="font-mono text-xs">{u.created_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="font-mono text-xs">{u.network.toUpperCase()}</td>
                      <td className="text-xs max-w-[260px] truncate" title={u.filename}>{u.filename}{u.duplicate_of && <Badge variant="warning" style={{marginLeft:6}}>DUP</Badge>}</td>
                      <td className="text-xs">{u.customer_name || <span className="text-zinc-400">auto</span>}</td>
                      <td className="font-mono text-xs">{u.period_start}{u.period_start !== u.period_end && ` → ${u.period_end}`}</td>
                      <td className="gf-num">{u.row_count}</td>
                      <td className="gf-num">{u.imported}</td>
                      <td className="gf-num">{u.updated}</td>
                      <td className="gf-num">{u.flagged > 0 ? <Badge variant="warning">{u.flagged}</Badge> : "—"}</td>
                      <td className="gf-num">{u.unmatched > 0 ? <Badge variant="error">{u.unmatched}</Badge> : "—"}</td>
                      <td className="gf-num">
                        {u.failed_rows > 0 ? (
                          <button
                            className="gf-btn gf-btn-ghost text-red-700"
                            onClick={() => openFailedRows(u)}
                            data-testid={`view-failed-rows-${u.upload_id}`}
                            title="Inspect rows that couldn't be imported"
                          >
                            <Badge variant="error">{u.failed_rows}</Badge>
                          </button>
                        ) : "—"}
                      </td>
                      <td className="text-right whitespace-nowrap">
                        <button
                          className="gf-btn gf-btn-ghost text-blue-700"
                          onClick={() => {
                            // Filter the underlying Transactions list to
                            // just this upload's rows so the operator can
                            // jump straight to a freshly-imported batch
                            // (USA April rows otherwise sit below today's
                            // CA rows on the default tran_date sort).
                            setFilters({ customer_id: "", network: "", country: "", status: "", start: "", end: "", upload_id: u.upload_id });
                            setShowUploads(false);
                          }}
                          data-testid={`view-tx-upload-${u.upload_id}`}
                          title="View only the transactions from this upload"
                        >
                          View rows
                        </button>
                        <button className="gf-btn gf-btn-ghost" onClick={() => setConfirmDelete(u)}
                          data-testid={`delete-tx-upload-${u.upload_id}`} title="Delete this upload and all its transactions">
                          <Trash size={14}/>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {uploads.length === 0 && <tr><td colSpan={12} className="text-center text-zinc-400 py-8">No transaction uploads yet.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between items-center gap-2 p-4 border-t border-zinc-200 bg-amber-50">
              <div className="text-xs text-amber-900">
                <span className="font-semibold">Re-import audit:</span> after a parser fix,
                re-upload a source CSV to recover rows that were silently dropped (dedup-safe).
              </div>
              <button
                className="gf-btn gf-btn-outline text-amber-800 border-amber-400 hover:bg-amber-100"
                onClick={() => setAuditModal({ open: true, file: null, network: "esso", country: "CA", dry_run: true, busy: false, result: null })}
                data-testid="open-reimport-audit-btn"
              >
                <FileText size={14}/> Re-import audit
              </button>
            </div>
            <div className="flex justify-between items-center gap-2 p-4 border-t border-zinc-200 bg-red-50">
              <div className="text-xs text-red-900">
                <span className="font-semibold">Danger zone:</span> wipe every transaction (including seeded/orphaned rows)
                and their upload history. Cannot be undone.
              </div>
              <button
                className="gf-btn gf-btn-outline text-red-700 border-red-400 hover:bg-red-100"
                onClick={() => { setShowUploads(false); openBulkDelete("all"); }}
                data-testid="reset-all-tx-button"
              >
                <Trash size={14}/> Reset all transactions
              </button>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setShowUploads(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ---- 2026-05-22 RE-IMPORT AUDIT MODAL ---- */}
      {auditModal.open && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !auditModal.busy && setAuditModal((s) => ({ ...s, open: false }))}
        >
          <div
            className="bg-white w-full max-w-2xl border border-zinc-200 max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="reimport-audit-modal"
          >
            <div className="gf-card-header">
              <h3>Re-import audit</h3>
              <div className="text-xs text-zinc-500 mt-1">
                Re-upload the original transaction CSV. Rows already in the database stay
                untouched; only rows that the parser now picks up (e.g. after a bug fix)
                will be inserted. Dedup uses the same 7-column rule as a regular import.
              </div>
            </div>
            <div className="p-4 space-y-4 overflow-auto flex-1">
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs space-y-1">
                  <span className="font-semibold text-zinc-700">Network</span>
                  <select
                    className="gf-input w-full"
                    value={auditModal.network}
                    onChange={(e) => setAuditModal((s) => ({ ...s, network: e.target.value }))}
                    disabled={auditModal.busy}
                    data-testid="audit-network-select"
                  >
                    <option value="esso">ESSO (Cardlock CA / USA)</option>
                    <option value="pipeline">Pipeline (CA)</option>
                    <option value="petro_pass">Petro-Pass (CA)</option>
                  </select>
                </label>
                {auditModal.network === "esso" && (
                  <label className="text-xs space-y-1">
                    <span className="font-semibold text-zinc-700">Country</span>
                    <select
                      className="gf-input w-full"
                      value={auditModal.country}
                      onChange={(e) => setAuditModal((s) => ({ ...s, country: e.target.value }))}
                      disabled={auditModal.busy}
                      data-testid="audit-country-select"
                    >
                      <option value="CA">Canada</option>
                      <option value="US">USA</option>
                    </select>
                  </label>
                )}
              </div>
              <label className="text-xs space-y-1 block">
                <span className="font-semibold text-zinc-700">Source CSV</span>
                <input
                  ref={auditFileRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="gf-input w-full text-xs"
                  onChange={(e) => setAuditModal((s) => ({ ...s, file: e.target.files?.[0] || null, result: null }))}
                  disabled={auditModal.busy}
                  data-testid="audit-file-input"
                />
                {auditModal.file && (
                  <div className="text-xs text-zinc-500 mt-1">
                    Selected: <span className="font-mono">{auditModal.file.name}</span>
                    {" · "}{Math.round(auditModal.file.size / 1024)} KB
                  </div>
                )}
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={auditModal.dry_run}
                  onChange={(e) => setAuditModal((s) => ({ ...s, dry_run: e.target.checked }))}
                  disabled={auditModal.busy}
                  data-testid="audit-dry-run-toggle"
                />
                <span>
                  <span className="font-semibold">Dry run</span> — preview what would be
                  inserted without writing anything. Recommended for the first pass.
                </span>
              </label>

              {auditModal.result && (
                <div className="border border-zinc-200 bg-zinc-50 p-3 text-xs space-y-2" data-testid="audit-result-panel">
                  <div className="font-semibold text-zinc-800">
                    Result {auditModal.result.dry_run && <Badge variant="warning" style={{ marginLeft: 4 }}>DRY RUN</Badge>}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono">
                    <div>Parsed rows</div>
                    <div className="text-right" data-testid="audit-parsed-count">{auditModal.result.parsed_rows}</div>
                    <div className="text-emerald-700">{auditModal.result.dry_run ? "Would insert" : "Inserted"}</div>
                    <div className="text-right text-emerald-700 font-semibold" data-testid="audit-inserted-count">{auditModal.result.inserted}</div>
                    <div>Already in DB</div>
                    <div className="text-right" data-testid="audit-skipped-existing-count">{auditModal.result.skipped_already_in_db}</div>
                    <div>Unmatched card</div>
                    <div className="text-right">{auditModal.result.skipped_unmatched_card}</div>
                    <div>Matched upload</div>
                    <div className="text-right text-xs truncate" title={auditModal.result.matched_upload_id || ""}>
                      {auditModal.result.matched_upload_id?.slice(0, 12) || <span className="text-zinc-400">none</span>}…
                    </div>
                  </div>
                  {auditModal.result.samples?.length > 0 && (
                    <div className="pt-2 border-t border-zinc-200">
                      <div className="font-semibold mb-1">Sample {auditModal.result.dry_run ? "candidates" : "recovered rows"} (first {auditModal.result.samples.length}):</div>
                      <div className="max-h-44 overflow-auto">
                        <table className="gf-table">
                          <thead>
                            <tr><th>DATE</th><th>TIME</th><th>INV</th><th>PROD</th><th className="text-right">QTY</th><th className="text-right">AMT</th><th>STATUS</th></tr>
                          </thead>
                          <tbody>
                            {auditModal.result.samples.map((s) => (
                              <tr key={s.transaction_id}>
                                <td className="font-mono">{s.tran_date}</td>
                                <td className="font-mono">{String(s.tran_time || "").slice(0, 5)}</td>
                                <td className="font-mono">{s.reference}</td>
                                <td>{s.product}</td>
                                <td className="gf-num">{s.qty}</td>
                                <td className="gf-num">{s.customer_amount}</td>
                                <td><Badge variant={s.status === "flagged" ? "warning" : "default"}>{s.status}</Badge></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-between items-center gap-2 p-4 border-t border-zinc-200 bg-zinc-50">
              <div className="text-xs text-zinc-500">
                {auditModal.dry_run
                  ? "No data will be written until you uncheck Dry run and run again."
                  : <span className="text-amber-700 font-semibold">Live mode — missing rows will be inserted on submit.</span>}
              </div>
              <div className="flex gap-2">
                <button
                  className="gf-btn gf-btn-outline"
                  disabled={auditModal.busy}
                  onClick={() => setAuditModal({ open: false, file: null, network: "esso", country: "CA", dry_run: true, busy: false, result: null })}
                  data-testid="audit-close-btn"
                >
                  Close
                </button>
                <button
                  className="gf-btn gf-btn-primary"
                  disabled={auditModal.busy || !auditModal.file}
                  onClick={runReimportAudit}
                  data-testid="audit-run-btn"
                >
                  {auditModal.busy
                    ? "Working…"
                    : auditModal.dry_run ? "Run dry-run" : "Recover missing rows"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* ---- PHASE 2.B FAILED-ROWS MODAL ---- */}
      {failedRows && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setFailedRows(null)}>
          <div className="bg-white w-full max-w-5xl border border-zinc-200 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="failed-rows-modal">
            <div className="gf-card-header">
              <h3>Failed rows · {failedRows.upload?.filename}</h3>
              <div className="text-xs text-zinc-500 mt-1">
                {failedRows.rows.length} row(s) couldn't be imported. The rest of the file imported normally.
              </div>
            </div>
            <div className="overflow-auto flex-1 p-4 space-y-3">
              {failedRows.busy && <div className="text-sm text-zinc-500">Loading…</div>}
              {!failedRows.busy && failedRows.rows.length === 0 && (
                <div className="text-sm text-zinc-400">No failed-row records (this upload predates Phase 2.B streaming or all rows imported cleanly).</div>
              )}
              {failedRows.rows.map((r, idx) => (
                <div key={idx} className="border border-red-200 bg-red-50 p-3 text-xs">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono text-red-700">row #{r.row_index}</span>
                    <Badge variant="error">{r.error_type}</Badge>
                    <span className="font-mono text-zinc-500">{r.created_at?.slice(0, 19).replace("T", " ")}</span>
                  </div>
                  <div className="text-red-900 mb-2"><b>Error:</b> {r.error_message}</div>
                  <details className="text-zinc-700">
                    <summary className="cursor-pointer text-zinc-600">Raw row data</summary>
                    <pre className="mt-1 p-2 bg-white border border-zinc-200 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(r.raw_row, null, 2)}</pre>
                  </details>
                  {r.traceback && (
                    <details className="text-zinc-700 mt-1">
                      <summary className="cursor-pointer text-zinc-600">Stack trace</summary>
                      <pre className="mt-1 p-2 bg-white border border-zinc-200 overflow-x-auto whitespace-pre-wrap">{r.traceback}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setFailedRows(null)} data-testid="close-failed-rows-modal">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ---- FLAG HELP MODAL ---- */}
      {showFlagHelp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowFlagHelp(false)}>
          <div className="bg-white w-full max-w-2xl border border-zinc-200 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>Transaction status &amp; row colour guide</h3></div>
            <div className="gf-card-body space-y-5 text-sm overflow-y-auto">
              <div>
                <div className="text-xs text-zinc-500 mb-2">
                  Every imported transaction is run through the pricing engine and tagged
                  with one of four <b>statuses</b>. The status drives whether the row will
                  be billed, and the row's background colour gives you a quick visual cue
                  of which rows still need attention.
                </div>
              </div>

              {/* ---- STATUSES ---- */}
              <div>
                <div className="overline mb-2">Statuses</div>

                <div className="border border-zinc-200 p-3 mb-2">
                  <div className="flex items-center gap-2"><Badge variant="info">pending</Badge><span className="text-xs text-zinc-500">white row</span></div>
                  <div className="mt-2 text-zinc-700">
                    Priced successfully and ready to invoice. The card matched a customer,
                    rack &amp; margin lookups both succeeded, and amounts were computed.
                    The next time you click <i>Generate invoices</i> these rows are picked up.
                  </div>
                </div>

                <div className="border border-zinc-200 p-3 mb-2">
                  <div className="flex items-center gap-2"><Badge variant="success">invoiced</Badge><span className="text-xs text-zinc-500">white row</span></div>
                  <div className="mt-2 text-zinc-700">
                    Already attached to an invoice and billed. Cannot be re-invoiced.
                    To remove a row from a finalised invoice, edit its status (<PencilSimple size={12} style={{display:"inline"}}/>)
                    back to <b>pending</b> — the system will void it from the invoice and recompute totals.
                  </div>
                </div>

                <div className="border border-yellow-300 p-3 mb-2" style={{ background: "#FEFCE8" }}>
                  <div className="flex items-center gap-2"><Badge variant="warning">flagged</Badge><span className="text-xs text-yellow-800">yellow row</span></div>
                  <div className="mt-2 text-zinc-700">
                    Card matched a customer, but the pricing engine couldn't confidently
                    price the row. <b>The row will still flow into the next invoice</b>, but
                    you should fix it first so the customer is billed correctly.
                  </div>
                  <div className="mt-2 text-zinc-700"><b>Common causes &amp; fixes:</b></div>
                  <ul className="list-disc ml-5 mt-1 text-zinc-600 space-y-1">
                    <li><span className="font-mono text-xs">Station not found in price list</span> — the station ID in the transaction file isn't in our rack price list. <b>Fix:</b> upload the latest price list from the supplier <i>or</i> add a station alias under <i>Price Lists → Station ID aliases</i>.</li>
                    <li><span className="font-mono text-xs">No margin rule matched</span> — the customer has no margin rule covering this station / city / province. <b>Fix:</b> open the customer profile and add a margin rule, or use the <i>Margins</i> page to upload a bulk template.</li>
                    <li><span className="font-mono text-xs">Grade resolved to DSL_EFF_LS</span> — informational note (not a problem). The supplier used a non-canonical grade label (e.g. "DSL EFF BS") and the engine canonicalised it.</li>
                  </ul>
                  <div className="mt-2 text-zinc-700">
                    After fixing, click <i>Reprice flagged</i> in the toolbar (re-runs the pricing
                    engine) or click <CheckCircle size={12} style={{display:"inline"}}/> on the row to mark it reviewed.
                  </div>
                </div>

                <div className="border border-red-300 p-3" style={{ background: "#FEE2E2" }}>
                  <div className="flex items-center gap-2"><Badge variant="error">unmatched</Badge><span className="text-xs text-red-800">pink row</span></div>
                  <div className="mt-2 text-zinc-700">
                    The card number in the transaction file isn't linked to any customer in the
                    registry. <b>The row will NOT be billed</b> until you assign a customer —
                    invoicing skips unmatched rows entirely.
                  </div>
                  <div className="mt-2 text-zinc-700"><b>Fix options:</b></div>
                  <ul className="list-disc ml-5 mt-1 text-zinc-600 space-y-1">
                    <li>Click the <PencilSimple size={12} style={{display:"inline"}}/> icon on the row → assign a customer + set status to <b>pending</b> in one go.</li>
                    <li>Add the card under <i>Fuel Cards → Add card</i> with the right customer, then click <i>Reprice flagged</i> (or re-import).</li>
                    <li>Delete the upload (Uploads modal) and re-import with a customer override if the whole file belongs to one customer.</li>
                  </ul>
                </div>

                <div className="border border-orange-300 p-3 mt-2" style={{ background: "#FFEDD5" }}>
                  <div className="flex items-center gap-2"><Badge variant="warning">duplicate</Badge><span className="text-xs text-orange-800">orange row</span></div>
                  <div className="mt-2 text-zinc-700">
                    A prior import already contains a transaction with the same Esso/Pipeline
                    <b> Invoice #</b> for the same card. <b>The row is excluded from invoicing</b>
                    so customers are never double-billed, but it's kept for audit so you can
                    decide whether to delete it.
                  </div>
                  <div className="mt-2 text-zinc-700">
                    <b>Match key (in order of precedence):</b>
                  </div>
                  <ul className="list-disc ml-5 mt-1 text-zinc-600 space-y-1">
                    <li>Same <b>Invoice # (REF)</b> + same network + same card → flagged immediately.</li>
                    <li>Missing Invoice # (some Pipeline rows) → match by date + station + product + qty + amount.</li>
                  </ul>
                  <div className="mt-2 text-zinc-700">
                    <b>Fix options:</b> delete the duplicate row (<Trash size={12} style={{display:"inline"}}/>),
                    or — if the duplicate is actually the correct one — delete the older
                    transaction first and re-import the file.
                  </div>
                </div>
              </div>

              {/* ---- ROW COLOURS ---- */}
              <div>
                <div className="overline mb-2">Row colour key</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="border border-zinc-200 p-2 text-xs">
                    <div className="w-full h-3 bg-white border border-zinc-200 mb-1"></div>
                    <b>White</b> — pending / invoiced (no action needed)
                  </div>
                  <div className="border border-zinc-200 p-2 text-xs">
                    <div className="w-full h-3 mb-1" style={{ background: "#FEFCE8" }}></div>
                    <b>Yellow</b> — flagged (will still bill, but review first)
                  </div>
                  <div className="border border-zinc-200 p-2 text-xs">
                    <div className="w-full h-3 mb-1" style={{ background: "#FEE2E2" }}></div>
                    <b>Pink / red</b> — unmatched (won't bill until fixed)
                  </div>
                  <div className="border border-zinc-200 p-2 text-xs">
                    <div className="w-full h-3 mb-1" style={{ background: "#FFEDD5" }}></div>
                    <b>Orange</b> — duplicate (excluded from invoicing)
                  </div>
                  <div className="border border-zinc-200 p-2 text-xs">
                    <div className="flex items-center gap-2"><span className="font-mono text-green-600">$1.68</span><span className="font-mono text-zinc-400">$0.00</span></div>
                    <b>GP column</b> — green = profit, grey = 0 / loss
                  </div>
                </div>
              </div>

              {/* ---- COLUMN GLOSSARY ---- */}
              <div>
                <div className="overline mb-2">Column glossary</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div><b>NET PRICE</b> — supplier base (excl. per-unit fuel taxes) + margin; matches the PDF report</div>
                  <div><b>MARGIN</b> — customer margin, already included in NET PRICE</div>
                  <div><b>PFT</b> — provincial fuel tax per unit (0 where not applicable)</div>
                  <div><b>LOCAL</b> — local/transit fuel tax per unit, e.g. BC (0 where not applicable)</div>
                  <div><b>UNIT PRICE</b> — NET PRICE + PFT + LOCAL, the price charged to the customer</div>
                  <div><b>QTY</b> — litres dispensed</div>
                  <div><b>AMT</b> — total amount (UNIT PRICE × QTY, in CAD)</div>
                  <div><b>GP</b> — gross profit ((UNIT PRICE − supplier cost) × QTY)</div>
                  <div><b>UNIT</b> — truck unit number from the customer's card profile (always overrides the transaction file)</div>
                  <div><b>CARD</b> — fuel card number as it appears on the transaction</div>
                </div>
              </div>

              {/* ---- WORKFLOW ---- */}
              <div className="border-t border-zinc-200 pt-3">
                <div className="overline mb-2">Recommended workflow</div>
                <ol className="list-decimal ml-5 text-xs text-zinc-700 space-y-1">
                  <li>Import the transaction file (Esso CSV / Pipeline XLS).</li>
                  <li>Filter <i>Status = unmatched</i> in the sidebar — assign customers via the <PencilSimple size={11} style={{display:"inline"}}/> icon, or add the missing cards under <i>Fuel Cards</i>.</li>
                  <li>Filter <i>Status = flagged</i> — fix the price list / margin rule, then click <i>Reprice flagged</i>.</li>
                  <li>Once the queue is clean, head to <i>Invoices → Generate invoices</i>.</li>
                </ol>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-primary" onClick={() => setShowFlagHelp(false)} data-testid="status-help-close">Got it</button>
            </div>
          </div>
        </div>
      )}

      {/* ---- SUSPECTED DUPLICATE SCAN MODAL ---- */}
      {scanDupes && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !scanDupes.busy && setScanDupes(null)}>
          <div className="bg-white w-full max-w-5xl border border-zinc-200 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header">
              <h3>Suspected duplicate transactions</h3>
              <div className="overline">{scanDupes.groups.length} GROUP(S) · {scanDupes.groups.reduce((n, g) => n + g.rows.length, 0)} ROW(S)</div>
            </div>
            <div className="gf-card-body overflow-y-auto space-y-4 text-sm">
              <div className="text-xs text-zinc-600">
                Each block below is a group of rows that look like duplicates of
                each other. The OLDEST row in each group is pre-unchecked (kept as
                the original); the rest are pre-checked to be marked as
                <Badge variant="warning"> duplicate</Badge>. Adjust your picks
                and click <b>Mark selected as duplicate</b>. Invoiced rows are
                skipped automatically by the server — void them first if you
                need to demote one.
              </div>
              {scanDupes.groups.map((g, gi) => (
                <div key={gi} className="border border-zinc-200">
                  <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-xs flex items-center justify-between">
                    <div>
                      <Badge variant={g.match_type === "reference" ? "info" : "warning"}>{g.match_type === "reference" ? "Invoice ref match" : "Row fingerprint"}</Badge>
                      <span className="ml-2 font-mono text-zinc-600">
                        {g.match_type === "reference"
                          ? `${g.match_key.network.toUpperCase()} · card ${formatCard(g.match_key.card_number, g.match_key.network)} · ref ${g.match_key.reference}`
                          : `${g.match_key.network.toUpperCase()} · card ${formatCard(g.match_key.card_number, g.match_key.network)} · ${g.match_key.tran_date} · ${g.match_key.product} · ${g.match_key.qty}L · $${g.match_key.original_amount}`}
                      </span>
                    </div>
                    <div className="text-zinc-500">{g.rows.length} rows</div>
                  </div>
                  <table className="gf-table">
                    <thead>
                      <tr>
                        <th></th><th>TX ID</th><th>DATE</th><th>CUSTOMER</th><th>STATION</th><th>PRODUCT</th>
                        <th className="text-right">QTY</th><th className="text-right">AMT</th><th>REF</th><th>STATUS</th><th>CREATED</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.rows.map((r, ri) => {
                        const isFirst = ri === 0;
                        const isInvoiced = r.status === "invoiced" || !!r.invoice_id;
                        return (
                          <tr key={r.transaction_id} style={isFirst ? { background: "#F0FDF4" } : {}}>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                disabled={isInvoiced}
                                checked={scanDupes.selected.has(r.transaction_id)}
                                onChange={() => toggleScanSelect(r.transaction_id)}
                                data-testid={`scan-pick-${r.transaction_id}`}
                                title={isInvoiced ? "Invoiced — void it first to demote" : isFirst ? "Oldest — usually the original to keep" : "Mark as duplicate"}
                              />
                            </td>
                            <td className="font-mono text-xs">{r.transaction_id.slice(0, 14)}</td>
                            <td className="font-mono text-xs">{r.tran_date}</td>
                            <td className="text-xs">{r.customer_name || <span className="text-zinc-400">—</span>}</td>
                            <td className="text-xs">{r.station_name}</td>
                            <td className="font-mono text-xs">{displayProduct(r.product, r.network)}</td>
                            <td className="gf-num">{Number(r.qty).toFixed(2)}</td>
                            <td className="gf-num">${Number(r.original_amount).toFixed(2)}</td>
                            <td className="font-mono text-xs">{r.reference || <span className="text-zinc-400">—</span>}</td>
                            <td><Badge variant={r.status === "invoiced" ? "success" : r.status === "flagged" ? "warning" : r.status === "unmatched" ? "error" : "info"}>{r.status}</Badge>{isFirst && <div className="text-[10px] text-green-700 mt-1">oldest · keep</div>}</td>
                            <td className="font-mono text-[10px] text-zinc-500">{(r.created_at || "").slice(0, 19).replace("T", " ")}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 p-4 border-t border-zinc-200">
              <div className="text-xs text-zinc-500">
                <b>{scanDupes.selected.size}</b> row(s) selected to be marked duplicate.
                Originals stay <Badge variant="info">pending</Badge>; invoiced rows are skipped.
              </div>
              <div className="flex gap-2">
                <button className="gf-btn gf-btn-outline" onClick={() => setScanDupes(null)} disabled={scanDupes.busy} data-testid="scan-cancel">Cancel</button>
                <button
                  className="gf-btn gf-btn-primary"
                  onClick={applyScanMark}
                  disabled={scanDupes.busy || scanDupes.selected.size === 0}
                  data-testid="scan-apply"
                >
                  {scanDupes.busy ? "Marking…" : `Mark ${scanDupes.selected.size} as duplicate`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* ---- CONFIRM DELETE UPLOAD MODAL ---- */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => { setConfirmDelete(null); setConfirmDeleteText(""); }}>
          <div className="bg-white w-full max-w-lg border border-red-300" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header bg-red-50 border-b border-red-200">
              <h3 className="flex items-center gap-2 text-red-800">
                <Warning size={16}/> Permanently delete upload?
              </h3>
            </div>
            <div className="gf-card-body text-sm space-y-3">
              <div><span className="overline">FILE</span><div className="font-mono mt-1 break-all" data-testid="confirm-delete-upload-filename">{confirmDelete.filename}</div></div>
              <div className="grid grid-cols-3 gap-3">
                <div><span className="overline">NETWORK</span><div className="font-mono mt-1">{confirmDelete.network.toUpperCase()}</div></div>
                <div><span className="overline">PERIOD</span><div className="font-mono mt-1">{confirmDelete.period_start}{confirmDelete.period_start !== confirmDelete.period_end && ` → ${confirmDelete.period_end}`}</div></div>
                <div><span className="overline">ROWS</span><div className="font-mono mt-1">{confirmDelete.row_count}</div></div>
              </div>

              <div className="bg-red-50 border border-red-200 px-3 py-2 mt-3 text-xs text-red-900 space-y-1">
                <div className="font-semibold">⚠ This action is permanent and cannot be recovered.</div>
                <div>
                  All {confirmDelete.row_count} transactions imported from this file will be
                  hard-deleted from the database — not soft-deleted, not in the Deleted
                  Records page, not restorable.
                </div>
                <div>
                  Uploads that already have invoiced transactions cannot be deleted.
                  Void the invoice first.
                </div>
              </div>

              <div>
                <div className="overline mb-1">Type to confirm</div>
                <div className="font-mono text-xs text-red-700 mb-1 select-all" data-testid="upload-delete-required-phrase">{REQUIRED_UPLOAD_DELETE_PHRASE}</div>
                <input
                  className="gf-input font-mono"
                  value={confirmDeleteText}
                  onChange={(e) => setConfirmDeleteText(e.target.value)}
                  placeholder="Type the phrase above exactly"
                  autoFocus
                  data-testid="confirm-delete-upload-phrase"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button
                className="gf-btn gf-btn-outline"
                onClick={() => { setConfirmDelete(null); setConfirmDeleteText(""); }}
                data-testid="confirm-delete-tx-upload-cancel"
              >
                Cancel
              </button>
              <button
                className="gf-btn gf-btn-danger"
                onClick={() => deleteUpload(confirmDelete)}
                disabled={confirmDeleteText.trim() !== REQUIRED_UPLOAD_DELETE_PHRASE}
                data-testid="confirm-delete-tx-upload"
              >
                <Trash size={14}/> Permanently delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ---- BULK DELETE CONFIRM MODAL ---- */}
      {bulkDelete && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={() => !bulkDelete.busy && setBulkDelete(null)}>
          <div className="bg-white w-full max-w-lg border border-red-300" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header bg-red-50 border-b border-red-200">
              <h3 className="text-red-900">
                {bulkDelete.mode === "all" ? "Reset all transactions" : "Delete shown transactions"}
              </h3>
            </div>
            <div className="gf-card-body text-sm space-y-3">
              {bulkDelete.mode === "filtered" ? (
                <div className="text-xs text-zinc-600">
                  This will delete every transaction matching the filters currently set on the page:
                  <div className="mt-2 p-2 bg-zinc-50 border border-zinc-200 font-mono text-[11px]">
                    {Object.entries(filters).filter(([, v]) => v).map(([k, v]) => (
                      <div key={k}><span className="text-zinc-500">{k}:</span> {v}</div>
                    ))}
                    {!hasAnyFilter && <div className="text-zinc-500">(no filter)</div>}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-zinc-600">
                  This will wipe <b>every</b> transaction in the system, plus their upload history.
                  Customers, fuel cards, fleet, price lists, and margin rules are <b>not</b> touched.
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 mt-2">
                <div className="border border-zinc-200 p-2">
                  <div className="overline">WILL DELETE</div>
                  <div className="text-xl font-mono font-semibold text-red-700">{bulkDelete.preview?.would_delete ?? 0}</div>
                </div>
                <div className="border border-zinc-200 p-2">
                  <div className="overline">INVOICED</div>
                  <div className="text-xl font-mono font-semibold">{bulkDelete.preview?.invoiced_in_scope ?? 0}</div>
                </div>
                <div className="border border-zinc-200 p-2">
                  <div className="overline">SKIPPED</div>
                  <div className="text-xl font-mono font-semibold text-amber-700">{bulkDelete.preview?.would_skip_invoiced ?? 0}</div>
                </div>
              </div>

              <label className="flex items-start gap-2 mt-2 text-xs cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={bulkDelete.force}
                  onChange={(e) => refreshBulkPreview(e.target.checked)}
                  data-testid="bulk-delete-force-checkbox"
                  className="mt-0.5"
                />
                <span>
                  <b className="text-red-800">Also delete invoiced transactions</b> (admin override).
                  Affected invoices will have their subtotal/tax/total recomputed; invoices that
                  end up empty are removed entirely.
                </span>
              </label>

              <div className="mt-3">
                <div className="border-2 border-red-500 bg-red-50 p-3 mb-3 text-xs text-red-900" data-testid="bulk-delete-irreversible-banner">
                  <b>This is irreversible.</b> Deleted rows are not soft-deleted — they cannot be undone without restoring a Mongo backup
                  (see <code>/app/MONGO_BACKUP.md</code>).
                </div>
                <div className="overline mb-1">TYPE THIS EXACT PHRASE TO CONFIRM</div>
                <div className="font-mono text-xs text-red-700 mb-1 select-all">I UNDERSTAND THIS IS IRREVERSIBLE</div>
                <input
                  type="text"
                  className="gf-input font-mono"
                  placeholder="Type the exact phrase above"
                  value={bulkDelete.confirmText}
                  onChange={(e) => setBulkDelete({ ...bulkDelete, confirmText: e.target.value })}
                  data-testid="bulk-delete-confirm-input"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button
                className="gf-btn gf-btn-outline"
                onClick={() => setBulkDelete(null)}
                disabled={bulkDelete.busy}
                data-testid="bulk-delete-cancel"
              >
                Cancel
              </button>
              <button
                className="gf-btn gf-btn-danger"
                onClick={runBulkDelete}
                disabled={bulkDelete.busy || bulkDelete.confirmText !== "I UNDERSTAND THIS IS IRREVERSIBLE" || (bulkDelete.preview?.would_delete ?? 0) === 0}
                data-testid="bulk-delete-run"
              >
                {bulkDelete.busy ? "Deleting…" : `Permanently delete ${bulkDelete.preview?.would_delete ?? 0}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkImport && (
        <BulkUploader
          open={showBulkImport}
          onClose={() => setShowBulkImport(false)}
          onAllDone={() => { load(); loadUploads(); }}
          title="Bulk import transactions"
          accept=".csv,.xls,.xlsx,.tsv,.txt"
          defaultMeta={{ network: "esso", customer_id: "" }}
          metaFields={[
            {
              key: "network",
              label: "NETWORK",
              type: "select",
              options: [
                { value: "esso", label: "Esso" },
                { value: "pipeline", label: "Pipeline" },
                { value: "petro_pass", label: "Petro-Pass" },
              ],
            },
            {
              key: "customer_id",
              label: "CUSTOMER (optional)",
              type: "select",
              options: [
                { value: "", label: "(auto from card)" },
                ...customers.map((c) => ({ value: c.customer_id, label: c.company_name })),
              ],
            },
          ]}
          detectMeta={(file) => {
            const n = file.name.toLowerCase();
            const meta = {};
            if (n.includes("pipeline") || n.endsWith(".xls") || n.endsWith(".xlsx")) meta.network = "pipeline";
            else if (n.includes("petro")) meta.network = "petro_pass";
            else if (n.includes("esso") || n.endsWith(".tsv") || n.endsWith(".csv") || n.endsWith(".txt")) meta.network = "esso";
            return meta;
          }}
          uploadOne={async (file, meta) => {
            const fd = new FormData();
            fd.append("network", meta.network || "esso");
            if (meta.customer_id) fd.append("customer_id", meta.customer_id);
            fd.append("file", file);
            const pf = (await api.post("/transactions/preflight", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
            if (!pf.can_import) throw new Error(`Validation blocked: ${(pf.critical || []).map((c) => c.code).join(", ")}`);
            if (pf.requires_confirmation) throw new Error(`Validation found ${pf.counts.review} review / ${pf.counts.errors} error row(s) — use single-file Import to review and confirm`);
            fd.append("preflight_token", pf.preflight_token);
            fd.append("preflight_id", pf.preflight_id);
            const { data } = await api.post("/transactions/import", fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            const dup = data.duplicate_of ? " · DUP" : "";
            return {
              summary: `${data.imported}↑ ${data.updated}↻ ${data.flagged}⚑ ${data.unmatched}?${dup}`,
            };
          }}
        />
      )}

      {/* ---- STATUS EDIT MODAL ---- */}
      {statusEdit && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" onClick={() => !statusEdit.busy && setStatusEdit(null)}>
          <div className="bg-white w-full max-w-lg border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header">
              <h3>Change transaction status</h3>
            </div>
            <div className="gf-card-body space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="overline">DATE</span><div className="font-mono mt-1">{statusEdit.tx.tran_date}</div></div>
                <div><span className="overline">CARD</span><div className="font-mono mt-1">{formatCard(statusEdit.tx.card_number, statusEdit.tx.network, statusEdit.tx.card_last4)}</div></div>
                <div><span className="overline">STATION</span><div className="font-mono mt-1">{statusEdit.tx.station_name || "—"}</div></div>
                <div><span className="overline">AMOUNT</span><div className="font-mono mt-1">${statusEdit.tx.customer_amount?.toFixed(2) ?? "—"}</div></div>
                <div><span className="overline">CURRENT</span><div className="mt-1"><Badge variant={statusEdit.tx.status === "invoiced" ? "success" : statusEdit.tx.status === "flagged" ? "warning" : statusEdit.tx.status === "unmatched" ? "error" : "info"}>{statusEdit.tx.status}</Badge></div></div>
                <div><span className="overline">INVOICE</span><div className="font-mono mt-1">{statusEdit.tx.invoice_id || "—"}</div></div>
              </div>

              <div>
                <div className="overline mb-1">New status</div>
                <select
                  className="gf-input"
                  value={statusEdit.status}
                  onChange={(e) => setStatusEdit({ ...statusEdit, status: e.target.value })}
                  data-testid="status-edit-select"
                >
                  <option value="pending">pending — priced, ready to invoice</option>
                  <option value="flagged">flagged — needs review</option>
                  <option value="unmatched">unmatched — no customer</option>
                </select>
                <div className="text-[11px] text-zinc-500 mt-1">
                  Invoicing is done via the Invoices page. Marking this row <b>pending</b> makes it
                  eligible for the next invoice run.
                  {statusEdit.tx.status === "invoiced" && (
                    <span className="text-red-700"> · Saving will <b>void</b> this row from invoice {statusEdit.tx.invoice_id} and recompute that invoice's totals.</span>
                  )}
                </div>
              </div>

              <div>
                <div className="overline mb-1">Customer</div>
                <select
                  className="gf-input"
                  value={statusEdit.customer_id}
                  onChange={(e) => setStatusEdit({ ...statusEdit, customer_id: e.target.value })}
                  data-testid="status-edit-customer"
                >
                  <option value="">— unassigned —</option>
                  {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.company_name}</option>)}
                </select>
                {statusEdit.tx.cross_network_hint && (
                  <div className="mt-2 p-2 border border-blue-200 bg-blue-50 text-xs text-blue-900 flex items-center justify-between gap-2">
                    <div>
                      Card <span className="font-mono">{formatCard(statusEdit.tx.card_number, statusEdit.tx.network, statusEdit.tx.card_last4)}</span> exists on <b>{statusEdit.tx.cross_network_hint.other_network.toUpperCase()}</b> for <b>{statusEdit.tx.cross_network_hint.customer_name}</b> — likely the same customer.
                    </div>
                    <button
                      type="button"
                      className="gf-btn gf-btn-outline text-blue-800 border-blue-400 hover:bg-blue-100 whitespace-nowrap"
                      onClick={() => setStatusEdit({ ...statusEdit, customer_id: statusEdit.tx.cross_network_hint.customer_id, status: "pending" })}
                      data-testid="status-edit-apply-cross-hint"
                    >
                      Use this
                    </button>
                  </div>
                )}
                <div className="text-[11px] text-zinc-500 mt-1">
                  Needed to move an <b>unmatched</b> row to <b>pending</b>. Reassigning the customer
                  on a matched row is also allowed (e.g. correcting a card that was mislinked).
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setStatusEdit(null)} disabled={statusEdit.busy} data-testid="status-edit-cancel">
                Cancel
              </button>
              <button className="gf-btn gf-btn-primary" onClick={saveStatusEdit} disabled={statusEdit.busy} data-testid="status-edit-save">
                {statusEdit.busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- DELETE SINGLE TRANSACTION CONFIRM ---- */}
      {delTx && (
        <div className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4" onClick={() => !delTx.busy && setDelTx(null)}>
          <div className="bg-white w-full max-w-md border border-red-300" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header bg-red-50 border-b border-red-200">
              <h3 className="text-red-900">Delete transaction?</h3>
            </div>
            <div className="gf-card-body space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="overline">DATE</span><div className="font-mono mt-1">{delTx.tx.tran_date}</div></div>
                <div><span className="overline">CARD</span><div className="font-mono mt-1">{formatCard(delTx.tx.card_number, delTx.tx.network, delTx.tx.card_last4)}</div></div>
                <div><span className="overline">STATION</span><div className="font-mono mt-1">{delTx.tx.station_name || "—"}</div></div>
                <div><span className="overline">AMOUNT</span><div className="font-mono mt-1">${delTx.tx.customer_amount?.toFixed(2) ?? "—"}</div></div>
                <div><span className="overline">STATUS</span><div className="mt-1"><Badge variant={delTx.tx.status === "invoiced" ? "success" : delTx.tx.status === "flagged" ? "warning" : delTx.tx.status === "unmatched" ? "error" : "info"}>{delTx.tx.status}</Badge></div></div>
                <div><span className="overline">INVOICE</span><div className="font-mono mt-1">{delTx.tx.invoice_id || "—"}</div></div>
              </div>
              <div className="text-xs text-zinc-600 pt-2">
                Permanently removes this single transaction.
                {delTx.tx.status === "invoiced" && (
                  <span className="text-red-700"> This row is on invoice <b>{delTx.tx.invoice_id}</b>; saving will recompute that invoice's subtotal/tax/total. If it's the only line, the invoice itself will be removed.</span>
                )}
              </div>
              {delTx.tx.status === "invoiced" && (
                <label className="flex items-start gap-2 mt-1 text-xs cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={delTx.force}
                    onChange={(e) => setDelTx({ ...delTx, force: e.target.checked })}
                    data-testid="delete-tx-force-checkbox"
                    className="mt-0.5"
                  />
                  <span><b className="text-red-800">I understand this will modify invoice {delTx.tx.invoice_id}.</b></span>
                </label>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setDelTx(null)} disabled={delTx.busy} data-testid="delete-tx-cancel">
                Cancel
              </button>
              <button
                className="gf-btn gf-btn-danger"
                onClick={runDeleteTx}
                disabled={delTx.busy || (delTx.tx.status === "invoiced" && !delTx.force)}
                data-testid="delete-tx-confirm"
              >
                {delTx.busy ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Cause #2 hardening (2026-05-29) — typed-confirmation modal for
          the Delete Duplicates flow. Operator must type exactly
          "DELETE N DUPLICATES" (N = preview.transaction_count) before
          the destructive button enables. Shows per-customer breakdown
          so the blast radius is unambiguous. */}
      {purgeModal.open && purgeModal.preview && (() => {
        const N = purgeModal.preview.transaction_count;
        const M = purgeModal.preview.customer_count;
        const required = `DELETE ${N} DUPLICATES`;
        const typedOk = purgeModal.typed.trim() === required;
        return (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => !purgeModal.busy && setPurgeModal({
              open: false, scope: null, customer_id: null,
              preview: null, typed: "", busy: false,
            })}
            data-testid="purge-duplicates-modal"
          >
            <div
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-zinc-900">
                    Soft-delete {N} duplicate transaction{N === 1 ? "" : "s"}
                  </h2>
                  <p className="text-sm text-zinc-600 mt-1">
                    {purgeModal.scope === "all"
                      ? "Scope: ALL customers (system-wide)"
                      : `Scope: single customer (${purgeModal.customer_id})`}
                    {" · "}affects {M} customer{M === 1 ? "" : "s"}
                  </p>
                </div>
              </div>

              <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                Rows are SOFT-deleted — recoverable for 30 days via the
                <strong> Undo last purge</strong> tile or the Deleted Records page.
                Originals (status: pending/invoiced/flagged) are NOT touched.
              </div>

              <div className="mb-4 border rounded max-h-60 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 sticky top-0">
                    <tr className="text-left text-zinc-600 border-b">
                      <th className="px-3 py-2">Customer</th>
                      <th className="px-3 py-2 text-right">Duplicate rows</th>
                    </tr>
                  </thead>
                  <tbody data-testid="purge-modal-customer-list">
                    {purgeModal.preview.customers.map((c) => (
                      <tr key={c.customer_id} className="border-b last:border-0">
                        <td className="px-3 py-2 font-mono text-xs">{c.company_name}</td>
                        <td className="px-3 py-2 text-right">{c.duplicate_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mb-2 text-sm text-zinc-700">
                Type <code className="bg-zinc-100 px-1 rounded">{required}</code> to enable the button:
              </div>
              <input
                type="text"
                value={purgeModal.typed}
                onChange={(e) => setPurgeModal((s) => ({ ...s, typed: e.target.value }))}
                disabled={purgeModal.busy}
                className="w-full border border-zinc-300 rounded px-3 py-2 font-mono text-sm"
                placeholder={required}
                data-testid="purge-modal-typed-input"
                autoFocus
              />

              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  className="gf-btn gf-btn-outline"
                  onClick={() => setPurgeModal({
                    open: false, scope: null, customer_id: null,
                    preview: null, typed: "", busy: false,
                  })}
                  disabled={purgeModal.busy}
                  data-testid="purge-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  className={`gf-btn ${typedOk ? "gf-btn-primary bg-red-600 hover:bg-red-700 border-red-700 text-white" : "gf-btn-outline opacity-50"}`}
                  onClick={executePurgeFromModal}
                  disabled={!typedOk || purgeModal.busy}
                  data-testid="purge-modal-confirm"
                >
                  {purgeModal.busy
                    ? "Deleting…"
                    : `Soft-delete ${N} duplicate${N === 1 ? "" : "s"}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <UndoBanner state={undoState} onClose={() => setUndoState(null)} />
    </SectionPad>
  );
}
